import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { createChart, ColorType, CrosshairMode, IChartApi } from "lightweight-charts";
import { SkipBack, ArrowRightToLine, TrendingUp } from "lucide-react";

import { buildTradeMarkers } from "../../lib/chart/buildTradeMarkers";
import type { TradeMarker } from "../../types/tradeMarkers";
import { normalizeBars } from "../../lib/chart/ChartDataAdapter";
import { type TradeExecution } from "../../lib/chart/MarkerBuilder";
import { normalizeExecutionsToMarkers } from "../../lib/chart/normalizeExecutionsToMarkers";
import type { ExecutionSeriesMarker } from "../../lib/chart/normalizeExecutionsToMarkers";
import { layoutExecutionMarkerBadges, type MarkerLabelBadge } from "../../lib/chart/markerLabelLayout";
import { computeTradeWindow, expandTradeWindowToSession } from "../../lib/chart/tradeWindow";
import useChartSettings from "../../hooks/useChartSettings";
import { buildChartOverlayIndicators, toggleChartIndicatorSettingId } from "../../config/chartIndicators";
import { normalizeTimestampToMs } from "../../lib/time/normalizeTimestampToMs";
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import {
    type ChartTzMode,
    filterBarsBySession,
    filterMarkersForDisplay,
    formatChartTime,
    formatChartTimeCompactDate,
} from "../../lib/chart/displayOptions";
import {
    detectTradeSession,
    executionNyDateKeys,
    getTradeSessionRangeSec,
    type TradeSessionStatus,
} from "../../lib/market/tradeSession";
import { DrawingManager } from "../../chart/drawings/DrawingManager";
import { deleteDrawing, getDrawings, upsertDrawing } from "../../chart/drawings/storage";
import type { DrawMode, Drawing, DrawingScope } from "../../chart/drawings/types";

import {
    Maximize2, Minimize2, Camera, Play, Pause, SkipForward, Save,
    ZoomIn, ZoomOut, ScanSearch, Eye, ChartCandlestick,
    ChartNoAxesColumn, Layers, Trash2, Crosshair, Type, Magnet, Lock,
    X
} from "../charts/Icons";

import { resolveExecutionRows } from "../../lib/trades/resolveExecutionRows";
import { getContractMultiplier } from "../../constants/contractMultipliers";
import { isIndexOrCfdInstrument } from "../../constants/indexInstruments";
import { getUsEquityMarketDateStatus } from '../../lib/market/usEquityMarketCalendar';
import {
    DEFAULT_REPLAY_BOUNDARY_MODE,
    buildReplayStepSnapshot,
    filterExecutionMarkersForReplay,
    getReplayMarkPrice,
    getReplayStartIndex,
    getReplayStepBoundaryMs,
    normalizeReplayExecutions,
} from "../../lib/trades/replayEngine";

import {
    getReplayRangeCapStartSec,
    getReplayRangeLabel,
    getReplayRangeTradingDays,
    isReplayTimeframeDisabled,
} from "../../lib/trades/replayRange";

import {
    Candle, TF, IndicatorConfig, pickSymbol, pickSecType, normalizeTf,
    fetchJsonWithTimeout, buildBaseCandidates,
    safeAddLineSeries, safeAddHistogramSeries, safeAddCandlestickSeries, safeRemoveSeries,
    downloadDataUrl
} from "../charts/chartUtils";
import { getSessionForAuthenticatedRequest } from "../../lib/auth";

import TradeChartStateBadge from '../chart/TradeChartStateBadge';
import { addVolumeSeries } from "../../lib/chartVolume";

import {
    calculateSMA, calculateEMA, calculateVWAP, calculateBollingerBands,
    calculateMACD, toHeikinAshi
} from "../charts/chartCalculations";
import {
    canUseBarsForDeterministicMetrics,
    extractTradeChartMeta,
    canUseBarsForReplay,
    getChartWarningMessage,
    getResolvedCandles,
    formatTradeChartLoadMessage,
    resolveTradeChartSource,
    toRequestTimeframe,
    type TradeChartFetchAttempt,
    type TradeChartResolutionResult,
} from '../../lib/chart/tradeChartResolution';
import { hasCoherentTradeInsightBars } from '../../lib/trades/tradeInsight';

const isDarkTheme = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

type ReplaySpeed = '1x' | '2x' | '3x' | '5x';

const mapPlaybackSpeedToReplaySpeed = (playbackSpeed: number): ReplaySpeed => {
    if (playbackSpeed <= 1) return '1x';
    if (playbackSpeed <= 2) return '2x';
    if (playbackSpeed <= 3.5) return '3x';
    return '5x';
};

const HorizontalLineNode = ({
    size = 30,
    color = 'currentColor',
    strokeWidth = 2,
    className = '',
    ...props
}: {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
} & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            {...props}
        >
            <circle cx="12" cy="12" r="2" />
            <line x1="3" y1="12" x2="10" y2="12" />
            <line x1="14" y1="12" x2="21" y2="12" />
        </svg>
    );
};

const TransformSquare = ({
    size = 32,
    color = 'currentColor',
    strokeWidth = 2,
    className = '',
    ...props
}: {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
} & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            {...props}
        >
            <circle cx="5" cy="5" r="2" />
            <circle cx="19" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <line x1="7" y1="5" x2="17" y2="5" />
            <line x1="7" y1="19" x2="17" y2="19" />
            <line x1="5" y1="7" x2="5" y2="17" />
            <line x1="19" y1="7" x2="19" y2="17" />
        </svg>
    );
};


const DiagonalLineNodes = ({
    size = 30,
    color = 'currentColor',
    strokeWidth = 2,
    className = '',
    ...props
}: {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
} & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            {...props}
        >
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="5" r="2" />
            <line x1="6.41" y1="17.59" x2="17.59" y2="6.41" />
        </svg>
    );
};

const ParallelChannelNodes = ({
  size = 30,
  color = 'currentColor',
  strokeWidth = 2,
  className = '',
  ...props
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      {/* Top Line Segment (Nodes on ends) */}
      <circle cx="4" cy="14" r="2" />
      <circle cx="14" cy="4" r="2" />
      <line x1="5.41" y1="12.59" x2="12.59" y2="5.41" />

      {/* Bottom Line Segment (Node in middle) */}
      <circle cx="15" cy="15" r="2" />
      <line x1="10" y1="20" x2="13.59" y2="16.41" />
      <line x1="16.41" y1="13.59" x2="20" y2="10" />
    </svg>
    );
};

const DiagonalRayNode = ({
    size = 30,
    color = 'currentColor',
    strokeWidth = 2,
    className = '',
    ...props
}: {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
} & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            {...props}
        >
            <circle cx="5" cy="19" r="2" />
            <circle cx="12" cy="12" r="2" />
            <line x1="6.41" y1="17.59" x2="10.59" y2="13.41" />
            <line x1="13.41" y1="10.59" x2="19" y2="5" />
        </svg>
    );
};

const VerticalLineNode = ({
    size = 30,
    color = 'currentColor',
    strokeWidth = 2,
    className = '',
    ...props
}: {
    size?: number;
    color?: string;
    strokeWidth?: number;
    className?: string;
} & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            {...props}
        >
            <circle cx="12" cy="12" r="2" />
            <line x1="12" y1="3" x2="12" y2="10" />
            <line x1="12" y1="14" x2="12" y2="21" />
        </svg>
    );
};

/**
 * IMPORTANT:
 * Chart ingestion must never use Date conversions.
 * All timestamps must already be canonical UNIX seconds UTC.
 */

function extractRawBars(payload: any): any[] {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.bars)) return payload.bars;
    if (Array.isArray(payload?.candles)) return payload.candles;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.bars && Array.isArray(payload.bars?.bars)) return payload.bars.bars;
    if (payload?.bars && Array.isArray(payload.bars?.data)) return payload.bars.data;
    return [];
}

function toTradeExecutions(trade: any): TradeExecution[] {
    const toCanonicalExecSec = (value: unknown): number | null => {
        const sec = toCanonicalUnixSec(value);
        return sec != null && Number.isFinite(sec) && sec > 0 ? sec : null;
    };

    const parseExecutionSide = (value: unknown): 'buy' | 'sell' | null => {
        const sideRaw = String(value ?? '').trim().toLowerCase();
        if (!sideRaw) return null;
        if (
            sideRaw === 's'
            || sideRaw.includes('sell')
            || sideRaw.includes('short')
            || sideRaw === '-'
            || sideRaw === '--'
        ) return 'sell';
        if (
            sideRaw === 'b'
            || sideRaw.includes('buy')
            || sideRaw.includes('long')
            || sideRaw === '+'
            || sideRaw === '++'
        ) return 'buy';
        return null;
    };

    const parseSideFromTrade = (): 'buy' | 'sell' => {
        const inferred = parseExecutionSide(trade?.side ?? trade?.direction ?? trade?.position ?? trade?.action);
        return inferred ?? 'buy';
    };

    const toQty = (value: unknown, fallback = 1): number => {
        const n = Number(value);
        if (!Number.isFinite(n) || n === 0) return fallback;
        return Math.max(1, Math.abs(n));
    };

    const rawExecutions = Array.isArray(trade?.executions)
        ? trade.executions
        : Array.isArray(trade?.executionsList)
            ? trade.executionsList
            : Array.isArray(trade?.fills)
                ? trade.fills
                : [];

    const fromExecutions = rawExecutions
        .map((exec: any) => {
            const inferredQty = Number(exec?.qty ?? exec?.quantity ?? exec?.shares ?? trade?.qty ?? trade?.quantity ?? trade?.volume);
            const side = parseExecutionSide(exec?.side ?? exec?.action ?? exec?.type ?? exec?.direction ?? exec?.position)
                ?? (Number.isFinite(inferredQty) && inferredQty !== 0 ? (inferredQty < 0 ? 'sell' : 'buy') : null);
            const timestamp = toCanonicalExecSec(
                exec?.timestamp
                ?? exec?.time
                ?? exec?.executedAt
                ?? exec?.date
                ?? exec?.t
                ?? exec?.openTimestampMs
                ?? exec?.closeTimestampMs
                ?? exec?.entryTime
                ?? exec?.exitTime
                ?? exec?.openTime
                ?? exec?.closeTime
            );
            if (!side || timestamp == null) return null;

            return {
                timestamp,
                side,
                qty: toQty(exec?.qty ?? exec?.quantity ?? exec?.shares ?? trade?.qty ?? trade?.quantity ?? trade?.volume, 1),
                price: Number(exec?.price ?? exec?.avgPrice ?? 0),
                isEntry: Boolean(exec?.isEntry ?? exec?.isOpen),
                isExit: Boolean(exec?.isExit ?? exec?.isClose),
                isOpen: Boolean(exec?.isOpen),
                rMultiple: Number.isFinite(Number(exec?.rMultiple)) ? Number(exec.rMultiple) : Number(trade?.rMultiple),
            } as TradeExecution;
        })
        .filter((exec: TradeExecution | null): exec is TradeExecution => exec !== null)
        .sort((a: TradeExecution, b: TradeExecution) => Number(a.timestamp) - Number(b.timestamp));

    const tradeOpenTs = toCanonicalExecSec(
        trade?.openTimestampMs
        ?? trade?.entryTimestampMs
        ?? trade?.openTime
        ?? trade?.entryTime
        ?? trade?.timestamp
        ?? trade?.date
    );
    const tradeCloseTs = toCanonicalExecSec(
        trade?.closeTimestampMs
        ?? trade?.exitTimestampMs
        ?? trade?.closeTime
        ?? trade?.exitTime
        ?? trade?.closedAt
    );

    if (fromExecutions.length > 0) {
        const annotated = fromExecutions.map((exec: TradeExecution, index: number, arr: TradeExecution[]) => ({
            ...exec,
            isEntry: Boolean(exec.isEntry) || index === 0,
            isExit: Boolean(exec.isExit) || index === arr.length - 1,
            isOpen: Boolean(exec.isOpen) || index === 0,
        }));

        if (tradeOpenTs != null && annotated[0]) {
            annotated[0] = {
                ...annotated[0],
                timestamp: tradeOpenTs,
                isEntry: true,
                isOpen: true,
            } as TradeExecution;
        }

        if (tradeCloseTs != null && annotated[annotated.length - 1]) {
            const closeIndex = annotated.length - 1;
            const minCloseTs = Number(annotated[0]?.timestamp ?? tradeCloseTs);
            annotated[closeIndex] = {
                ...annotated[closeIndex],
                timestamp: Math.max(tradeCloseTs, minCloseTs),
                isExit: true,
                isOpen: false,
            } as TradeExecution;
        }

        annotated.sort((a: TradeExecution, b: TradeExecution) => Number(a.timestamp) - Number(b.timestamp));
        return annotated;
    }

    const openTs = tradeOpenTs;
    const closeTs = tradeCloseTs;

    const qty = toQty(trade?.qty ?? trade?.quantity ?? trade?.volume ?? trade?.shares, 1);
    const side = parseSideFromTrade();
    const entryPrice = Number(trade?.entryPrice ?? trade?.avgEntryPrice ?? trade?.avgEntry);
    const exitPrice = Number(trade?.exitPrice ?? trade?.avgExitPrice ?? trade?.avgExit);

    const synthetic: TradeExecution[] = [];
    if (openTs != null) {
        synthetic.push({
            timestamp: openTs,
            side,
            qty,
            price: Number.isFinite(entryPrice) ? entryPrice : 0,
            isEntry: true,
            isExit: false,
            isOpen: true,
            rMultiple: Number.isFinite(Number(trade?.rMultiple)) ? Number(trade.rMultiple) : undefined,
        } as TradeExecution);
    }

    if (closeTs != null && (openTs == null || closeTs >= openTs)) {
        const closeSide: 'buy' | 'sell' = side === 'buy' ? 'sell' : 'buy';
        synthetic.push({
            timestamp: closeTs,
            side: closeSide,
            qty,
            price: Number.isFinite(exitPrice) ? exitPrice : 0,
            isEntry: false,
            isExit: true,
            isOpen: false,
            rMultiple: Number.isFinite(Number(trade?.rMultiple)) ? Number(trade.rMultiple) : undefined,
        } as TradeExecution);
    }

    return synthetic.sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
}

// helper `withBadgeAlpha` removed — it was declared but never used

function markerConnectorColor(color: string): string {
    const c = String(color || '').trim().toLowerCase();
    if (c === '#16a34a' || c === '#4ade80' || c === '#22c55e' || c === '#15803d') return 'rgba(74, 222, 128, 0.64)';
    if (c === '#dc2626' || c === '#f87171' || c === '#ef4444') return 'rgba(248, 113, 113, 0.66)';
    if (c === '#f59e0b') return 'rgba(245, 158, 11, 0.62)';
    return 'rgba(203, 213, 225, 0.58)';
}

function toCanonicalUnixSec(value: unknown): number | null {
    if (value == null) return null;
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const asNum = Number(trimmed);
        if (Number.isFinite(asNum) && asNum > 0) {
            return asNum >= 100000000000 ? Math.floor(asNum / 1000) : Math.floor(asNum);
        }
        const parsed = Date.parse(trimmed);
        return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
    }

    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n >= 100000000000 ? Math.floor(n / 1000) : Math.floor(n);
}

function toUnixSecForChartTime(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value);
    if (value && typeof value === 'object') {
        const t = (value as any).timestamp;
        if (typeof t === 'number' && Number.isFinite(t)) return Math.floor(t);
        const y = Number((value as any).year);
        const m = Number((value as any).month);
        const d = Number((value as any).day);
        if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
            return Math.floor(Date.UTC(y, m - 1, d) / 1000);
        }
    }
    return null;
}

function detectPriceFormat(symbol: string, secType: string): { precision: number; minMove: number } {
    const normalizedSymbol = String(symbol || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    const normalizedSecType = String(secType || '').trim().toUpperCase();
    const root = normalizedSymbol.match(/^([A-Z0-9]{1,4})/)?.[1] ?? normalizedSymbol;
    const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);

    const precisionFromMinMove = (minMove: number) => {
        if (!Number.isFinite(minMove) || minMove <= 0) return 2;
        const asText = minMove.toString();
        if (!asText.includes('.')) return 0;
        return asText.split('.')[1].length;
    };

    if (normalizedSecType === 'CRYPTO') {
        return { precision: 2, minMove: 0.01 };
    }

    if (/^[A-Z]{6}$/.test(normalizedSymbol)) {
        const base = normalizedSymbol.slice(0, 3);
        const quote = normalizedSymbol.slice(3);
        if (cryptoRoots.has(base) || cryptoRoots.has(quote)) {
            return { precision: 2, minMove: 0.01 };
        }
    }

    if (normalizedSecType === 'CASH' || /^[A-Z]{6}$/.test(normalizedSymbol)) {
        return { precision: 5, minMove: 0.00001 };
    }

    if (normalizedSecType === 'FUT' || normalizedSecType === 'FUTURES') {
        const futuresMinMoveByRoot: Record<string, number> = {
            '6E': 0.00005,
            '6B': 0.0001,
            '6A': 0.0001,
            '6C': 0.0001,
            '6J': 0.000001,
            '6N': 0.0001,
            '6S': 0.0001,
        };
        const minMove = futuresMinMoveByRoot[root] ?? 0.01;
        const precision = Math.max(2, precisionFromMinMove(minMove));
        return { precision, minMove };
    }

    return { precision: 2, minMove: 0.01 };
}

type PolicyTf = '1m' | '5m' | '15m' | '30m' | '60m' | '1d';

const POLICY_TO_TF: Record<PolicyTf, TF> = {
    "1m": "1min",
    "5m": "5min",
    "15m": "15min",
    "30m": "30min",
    "60m": "60min",
    "1d": "1d",
};

const TF_TO_POLICY: Record<TF, PolicyTf> = {
    "1min": "1m",
    "2min": "1m",
    "3min": "1m",
    "5min": "5m",
    "10min": "5m",
    "15min": "15m",
    "30min": "30m",
    "60min": "60m",
    "1d": "1d",
};

const TIMEFRAME_POLICY_ORDER = ["1m", "5m", "15m", "30m", "60m", "1d"] as const;

export function pickNextEnabledPolicy(current: PolicyTf, valid: Record<string, boolean>): PolicyTf {
    const idx = TIMEFRAME_POLICY_ORDER.indexOf(current);
    for (let i = idx + 1; i < TIMEFRAME_POLICY_ORDER.length; i += 1) {
        const candidate = TIMEFRAME_POLICY_ORDER[i];
        if (valid[candidate]) return candidate;
    }
    for (let i = 0; i <= idx; i += 1) {
        const candidate = TIMEFRAME_POLICY_ORDER[i];
        if (valid[candidate]) return candidate;
    }
    return '1d';
}

// --- HELPERS ---
const TooltipButton = ({ onClick, title, children, active, className = "", side = "bottom" }: any) => {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number; transform?: string } | null>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const [dark, setDark] = useState<boolean>(() => isDarkTheme());
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const updateTheme = () => setDark(isDarkTheme());
        const observer = new MutationObserver(() => updateTheme());
        try {
            observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        } catch {
            return;
        }
        updateTheme();
        return () => {
            try { observer.disconnect(); } catch {}
        };
    }, []);

    const updatePos = useCallback(() => {
        const el = btnRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const gap = 8;

        if (side === "right") {
          setPos({ top: rect.top + rect.height / 2, left: rect.right + gap, transform: "translateY(-50%)" });
          return;
        }
        if (side === "left") {
          setPos({ top: rect.top + rect.height / 2, left: rect.left - gap, transform: "translate(-100%, -50%)" });
          return;
        }
        if (side === "top") {
          setPos({ top: rect.top - gap, left: rect.left + rect.width / 2, transform: "translate(-50%, -100%)" });
          return;
        }
        // bottom (default)
        setPos({ top: rect.bottom + gap + 12, left: rect.left + rect.width / 2, transform: "translateX(-50%)" });
      }, [side]);

      useEffect(() => {
        if (!show) return;
        updatePos();
        const onScroll = () => updatePos();
        const onResize = () => updatePos();
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        return () => {
          window.removeEventListener("scroll", onScroll, true);
          window.removeEventListener("resize", onResize);
        };
      }, [show, updatePos]);

    return (
        <div className="flex items-center justify-center">
            <button
                ref={btnRef}
                onClick={onClick}
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                className={`p-1.5 rounded transition-colors ${dark ? "hover:bg-[#2a2e39]" : "hover:bg-blue-200 hover:text-blue-600"} ${active ? (dark ? "bg-[#2962ff] text-white" : "bg-blue-600 text-white") : (dark ? "text-gray-400 hover:text-white" : "text-zinc-500") } ${className}`}
            >
                {children}
            </button>
            {show && pos && createPortal(
                <div
                    style={{
                        position: "fixed",
                        top: pos.top,
                        left: pos.left,
                        transform: pos.transform,
                            zIndex: 2147483647,
                    }}
                    className={dark ? "px-2 py-1 bg-blue-600 text-white text-[10px] rounded shadow-sm whitespace-nowrap pointer-events-none" : "px-2 py-1 bg-zinc-900 text-white text-[10px] rounded shadow-sm whitespace-nowrap pointer-events-none"}
                >
                    {title}
                </div>,
                document.body
            )}
        </div>
    );
};

// exported helper so behavior can be unit tested
export function isGlobalDeleteAllowed(e: KeyboardEvent, editingNoteId: number | null): boolean {
  if (e.key !== 'Delete' && e.key !== 'Backspace') return false;
  if (editingNoteId != null) return false;
    const isTypingEl = (el: HTMLElement | null | undefined): boolean => {
        if (!el) return false;
        if (el.matches('input,textarea,select')) return true;
        if (el.isContentEditable) return true;
        if (String((el as any).contentEditable || '').toLowerCase() === 'true') return true;
        if (String(el.getAttribute('contenteditable') || '').toLowerCase() === 'true') return true;
        if (el.closest('[contenteditable="true"]')) return true;
        if (el.closest('[contenteditable]')) return true;
        return false;
    };
    const pathTarget = (e as any)?.composedPath?.()?.[0] as HTMLElement | null | undefined;
    const target = (e.target as HTMLElement | null) ?? pathTarget ?? null;
    const active = document.activeElement as HTMLElement | null;
    if (isTypingEl(target) || isTypingEl(active)) return false;
  return true;
}

export function syncDrawingManagerBarTimes(
    manager: { setBarTimes: (times: Array<number | string | Date>) => void; invalidateOverlay: () => void } | null | undefined,
    displayedBars: Array<{ time: unknown }> | null | undefined,
): void {
    if (!manager) return;
    const times = (displayedBars || [])
        .map((bar) => Number((bar as any)?.time))
        .filter((t) => Number.isFinite(t));
    // prefer calling setBars when available so snapping can use OHLC values
    try {
        if ((manager as any).setBars) (manager as any).setBars(displayedBars || []);
    } catch {}
    manager.setBarTimes(times);
    manager.invalidateOverlay();
}

export function isDemoTrade(trade: any): boolean {
    if (!trade || typeof trade !== 'object') return false;
    if (trade.isDemo === true || trade.demo === true) return true;
    const source = String(trade.source ?? '').trim().toLowerCase();
    const sourceType = String(trade.sourceType ?? '').trim().toLowerCase();
    if (source === 'demo' || sourceType === 'demo') return true;
    const id = String(trade.id ?? '').trim().toLowerCase();
    const tradeId = String(trade.tradeId ?? '').trim().toLowerCase();
    const storageId = String(trade.storageId ?? '').trim().toLowerCase();
    if (id.startsWith('demo_') || id.startsWith('demo-')) return true;
    if (tradeId.startsWith('demo_') || tradeId.startsWith('demo-')) return true;
    if (storageId.startsWith('demo_') || storageId.startsWith('demo-')) return true;
    return false;
}

function TradeReplayChart(props: any) {
    const {
        timeframe: propTimeframe,
        exchange: propExchange,
        currency: propCurrency,
        trade: propTrade,
        selectedTrade: propSelectedTrade,
        displaySymbol: propDisplaySymbol,
        onReplayStateChange,
        focusExecutionRequest,
        isDemo: propIsDemo,
    } = props || {};

    const symbol = pickSymbol(props);
    const secType = pickSecType(props);
    const tradeForMarkers = propSelectedTrade ?? propTrade;
    // Extracted to avoid tradeWindowBounds depending on the full `props` object reference,
    // which changes identity on every parent render and would cause spurious effect re-fires.
    const propExecutions = (props as any)?.executions;
    const secTypeUpper = useMemo(() => {
        const raw = String(secType || '').toUpperCase();
        if (raw === 'FUTURES') return 'FUT';
        return raw;
    }, [secType]);
    const activeTradeId = useMemo(() => {
        const raw = tradeForMarkers?.id ?? tradeForMarkers?._id ?? tradeForMarkers?.tradeId ?? props?.tradeId ?? props?.selectedTradeId;
        const value = raw == null ? '' : String(raw).trim();
        return value || undefined;
    }, [tradeForMarkers, props?.tradeId, props?.selectedTradeId]);

    const isDemoTradeActive = useMemo(() => {
        return propIsDemo === true || isDemoTrade(tradeForMarkers);
    }, [propIsDemo, tradeForMarkers]);
    const defaultChartResolution = useMemo<TradeChartResolutionResult>(() => ({
        chartMode: 'unavailable',
        assetClass: secTypeUpper === 'FUTURES' ? 'FUT' : (secTypeUpper === 'CASH' || secTypeUpper === 'CRYPTO' || secTypeUpper === 'FUT' ? secTypeUpper as any : 'STK'),
        requestedSymbol: symbol || '',
        resolvedSymbol: symbol || '',
        requestedTimeframe: '1m',
        resolvedTimeframe: '1m',
        exactInstrument: secTypeUpper !== 'FUT',
        exactTimeframe: true,
        barsAvailable: false,
        replayAllowed: false,
        metricsAllowed: false,
        fallbackReason: null,
        warningCode: null,
        warningMessage: null,
        sourceType: 'none',
    }), [secTypeUpper, symbol]);

    const dark = isDarkTheme();

  // State
    const [rawData, setRawData] = useState<Candle[]>([]);
  const [allData, setAllData] = useState<Candle[]>([]);
  const [displayedData, setDisplayedData] = useState<Candle[]>([]);

  const [timeframe, setTimeframe] = useState<TF>(normalizeTf(propTimeframe ?? "1min"));
    const [candleMode, setCandleMode] = useState<"candle" | "heikin">("candle");
    const [showVolume, setShowVolume] = useState(false);
        const [chartSettings, setChartSettings] = useChartSettings();
        useEffect(() => {
            setCandleMode(chartSettings.chartType === 'heikin' ? 'heikin' : 'candle');
        }, [chartSettings.chartType]);
        const tradeMarkersVisible = chartSettings.showTradeMarkers;
            const executionMarkersVisible = tradeMarkersVisible && chartSettings.showExecutions;
        const pnlOverlayVisible = chartSettings.showPnlOverlay;
        const showTradeDrawings = chartSettings.showTradeDrawings;
            const volumeVisible = showVolume && !chartSettings.hideVolumeFromCharts;
    // DEV BUILD STAMP — proves HMR delivered this code bundle. Remove before shipping.
    const DEV_BUILD_ID = '2026-08-03T11:06Z';
    const displayTimeZone = useDisplayTimezone();
    const tzMode: ChartTzMode = 'local';
    const displayTimeZoneRef = useRef(displayTimeZone);
    useEffect(() => {
        displayTimeZoneRef.current = displayTimeZone;
        console.log('[TradeReplayChart][timezone]', displayTimeZone, 'symbol:', symbol, 'build:', DEV_BUILD_ID);
    }, [displayTimeZone]);
  const [showIndDropdown, setShowIndDropdown] = useState(false);
  const [showSaveMsg, setShowSaveMsg] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState<string>("");
    const [toolWarningMsg, setToolWarningMsg] = useState<string>("");
  const [latestMeta, setLatestMeta] = useState<any | null>(null);
    // Replay usage model derived from OHLC meta (meta.replayUsage)
    const replayUsageModel = useMemo(() => {
        const meta = latestMeta && typeof latestMeta === 'object' ? latestMeta as Record<string, any> : null;
        const replay = meta?.replayUsage && typeof meta.replayUsage === 'object' ? meta.replayUsage as Record<string, any> : null;
        if (!replay) return null;

        // If usage write status explicitly indicates a failure/skip, do not show the badge.
        const writeStatusRaw = replay.usageWriteStatus ?? '';
        const writeStatus = typeof writeStatusRaw === 'string' ? writeStatusRaw.trim().toLowerCase() : String(writeStatusRaw).toLowerCase();
        if (writeStatus === 'failed' || writeStatus === 'skipped' || writeStatus === 'error') return null;

        // Accept boolean true, numeric 1, or truthy string 'true'/'1'/'yes'
        const applicableRaw = replay.applicable;
        let applicable = false;
        if (applicableRaw === true) applicable = true;
        else if (typeof applicableRaw === 'string') {
            const s = applicableRaw.trim().toLowerCase();
            if (s === 'true' || s === '1' || s === 'yes') applicable = true;
        } else if (Number.isFinite(Number(applicableRaw)) && Number(applicableRaw) === 1) {
            applicable = true;
        }

        const allowanceGranted = Number.isFinite(Number(replay.allowanceGranted)) ? Number(replay.allowanceGranted) : null;
        const used = Number.isFinite(Number(replay.used)) ? Number(replay.used) : null;
        let remaining = Number.isFinite(Number(replay.remaining)) ? Number(replay.remaining) : null;
        if (remaining == null && allowanceGranted != null && used != null) remaining = allowanceGranted - used;
        if (remaining != null) remaining = Math.max(0, remaining);

        if (!applicable || allowanceGranted == null || used == null) return null;
        const isExhausted = remaining != null && remaining <= 0;
        const isNearLimit = remaining != null && remaining > 0 && remaining <= 5;
        return { applicable, allowanceGranted, used, remaining, isExhausted, isNearLimit } as {
            applicable: boolean;
            allowanceGranted: number;
            used: number;
            remaining: number | null;
            isExhausted: boolean;
            isNearLimit: boolean;
        };
    }, [latestMeta]);
    const replayUsageRemainingDisplay = replayUsageModel
        ? (typeof replayUsageModel.remaining === 'number'
            ? replayUsageModel.remaining
            : Math.max(0, replayUsageModel.allowanceGranted - replayUsageModel.used))
        : null;
    const replayUsageBadgeHelp = replayUsageModel && replayUsageRemainingDisplay != null
        ? `Replay uses left: ${replayUsageRemainingDisplay} of ${replayUsageModel.allowanceGranted}. Replay uses are counted when you start replay on 1m, 5m, 15m, 30m, or 1H charts. Repeating the same replay does not count twice.`
        : undefined;
    const [chartResolution, setChartResolution] = useState<TradeChartResolutionResult>(defaultChartResolution);
    const [timeframeAdjustedMsg, setTimeframeAdjustedMsg] = useState<string>("");
    const [chartInitError, setChartInitError] = useState<string | null>(null);
    const [chartInitNonce, setChartInitNonce] = useState(0);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isReplayMode, setIsReplayMode] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
        const initialReplaySpeed = mapPlaybackSpeedToReplaySpeed(chartSettings.playbackSpeed);
        const [replaySpeed, setReplaySpeed] = useState<ReplaySpeed>(initialReplaySpeed);
        const replaySpeedRef = useRef<ReplaySpeed>(initialReplaySpeed);
        const chartPlaybackSpeedRef = useRef<ReplaySpeed>(initialReplaySpeed);
        const [drawMode, setDrawMode] = useState<DrawMode>("select");
    const [symbolDrawings, setSymbolDrawings] = useState<Drawing[]>([]);
    const [tradeDrawings, setTradeDrawings] = useState<Drawing[]>([]);
    const [newDrawingScope, setNewDrawingScope] = useState<DrawingScope>('symbol');
  const [hoverData, setHoverData] = useState<any>(null);
    const [markerBadges, setMarkerBadges] = useState<MarkerLabelBadge[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
    const scrollLocked = chartSettings.lockScrolling;
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });


  const [notes, setNotes] = useState<any[]>([]);
  const [draggingNoteId, setDraggingNoteId] = useState<number | null>(null);
  const [draggedLine, setDraggedLine] = useState<any>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>("");
  const [lineEdit, setLineEdit] = useState<{ line: any; x: number; y: number; value: string } | null>(null);

  const [magnetEnabled, setMagnetEnabled] = useState(false);
  const [drawingsLocked, setDrawingsLocked] = useState(false);
  const [drawingsHidden, setDrawingsHidden] = useState(false);
    const [hoveredTradeMarker, setHoveredTradeMarker] = useState<TradeMarker | null>(null);
        const [focusedExecutionId, setFocusedExecutionId] = useState<string | null>(null);
        const [focusPulseNonce, setFocusPulseNonce] = useState(0);

      useEffect(() => {
          replaySpeedRef.current = replaySpeed;
      }, [replaySpeed]);

      useEffect(() => {
          const nextReplaySpeed = mapPlaybackSpeedToReplaySpeed(chartSettings.playbackSpeed);
          if (replaySpeedRef.current === chartPlaybackSpeedRef.current && nextReplaySpeed !== replaySpeedRef.current) {
              replaySpeedRef.current = nextReplaySpeed;
              setReplaySpeed(nextReplaySpeed);
          }
          chartPlaybackSpeedRef.current = nextReplaySpeed;
      }, [chartSettings.playbackSpeed]);

    const indicators = useMemo<IndicatorConfig[]>(
        () => buildChartOverlayIndicators(chartSettings.defaultIndicatorIds),
        [chartSettings.defaultIndicatorIds],
    );
    const showMACD = chartSettings.defaultIndicatorIds.includes('macd');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
    const chartAreaRef = useRef<HTMLDivElement>(null);
  const macdContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  const macdChartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<any>(null);
    const replayBreakEvenLineRef = useRef<any>(null);
    const volumeSeriesRef = useRef<ReturnType<typeof addVolumeSeries> | null>(null);
  const lineSeriesRefs = useRef<Map<string, any>>(new Map());
  const macdSeriesRefs = useRef<any>({});
    const drawingsRef = useRef<Array<{ kind: 'hline'; line: any } | { kind: 'vline'; series: any }>>([]);
    const drawingManagerRef = useRef<DrawingManager | null>(null);
    const symbolDrawingsRef = useRef<Drawing[]>([]);
    const tradeDrawingsRef = useRef<Drawing[]>([]);
  const replayTimerRef = useRef<number | null>(null);
        const scrubberRef = useRef<HTMLDivElement | null>(null);
  const indBtnRef = useRef<HTMLButtonElement>(null);
  const draggedLineRef = useRef<any>(null);
  const notesRef = useRef<any[]>([]);
// (removed unused lastClickRef)
  const abortRef = useRef<AbortController | null>(null);
  const chosenBaseRef = useRef<string | null>(null);
    const baseProbePromiseRef = useRef<Promise<string | null> | null>(null);
    const inFlightRequestKeyRef = useRef<string | null>(null);
  const retryTimerRef = useRef<number | null>(null);
        const toolWarningTimerRef = useRef<number | null>(null);
    const prevTimeframeRef = useRef<string | null>(null);
    const pendingExecutionAutoFocusRef = useRef(false);
    const focusPulseTimerRef = useRef<number | null>(null);
    const lastHandledFocusKeyRef = useRef<string | null>(null);
    const dataVersionRef = useRef(0);
    const volumeByTimeRef = useRef<Map<number, number>>(new Map());
    const candleByTimeRef = useRef<Map<number, Candle>>(new Map());
        const markersEnabledRef = useRef(tradeMarkersVisible);
        const visibleMarkersRef = useRef<TradeMarker[]>([]);
        const markerHoverIdRef = useRef<string | null>(null);
        const markerToleranceSecRef = useRef<number>(60);
        const markerOverlayRafRef = useRef<number | null>(null);



    const DEFAULT_HIST_COLOR = "#26a69a";

    const safeNum = (n: unknown) => {
        const x = typeof n === "number" ? n : Number(n);
        return Number.isFinite(x) ? x : 0;
    };

    const safeBarColor = (c: unknown) =>
        (typeof c === "string" && c.trim().length ? c.trim() : undefined);


  const drawModeRef = useRef(drawMode);
  const drawingsLockedRef = useRef(drawingsLocked);
        const timeframeMsgTimerRef = useRef<number | null>(null);

    const canonicalExecutionUnixSec = useMemo(() => {
        const direct = toCanonicalUnixSec(props?.executionUnixSec);
        if (direct != null) return direct;

        const executionArrays = [
            Array.isArray(props?.executions) ? props.executions : null,
            Array.isArray(tradeForMarkers?.executions) ? tradeForMarkers.executions : null,
            Array.isArray(tradeForMarkers?.executionsList) ? tradeForMarkers.executionsList : null,
            Array.isArray(tradeForMarkers?.fills) ? tradeForMarkers.fills : null,
        ].filter((arr): arr is any[] => Array.isArray(arr));

        for (const arr of executionArrays) {
            for (const exec of arr) {
                const sec = toCanonicalUnixSec(
                    exec?.timestamp
                    ?? exec?.time
                    ?? exec?.executedAt
                    ?? exec?.date
                    ?? exec?.t
                    ?? exec?.closeTimestampMs
                    ?? exec?.exitTimestampMs
                    ?? exec?.closeTime
                    ?? exec?.exitTime
                    ?? exec?.closeDate
                    ?? exec?.exitDate
                    ?? exec?.closedAt
                );
                if (sec != null) return sec;
            }
        }

        const candidate =
            tradeForMarkers?.openTimestampMs
            ?? tradeForMarkers?.entryTimestampMs
            ?? tradeForMarkers?.openTime
            ?? tradeForMarkers?.entryTime
            ?? tradeForMarkers?.timestamp
            ?? tradeForMarkers?.date
            ?? tradeForMarkers?.closeTimestampMs
            ?? tradeForMarkers?.closeTime
            ?? tradeForMarkers?.closedAt;

        const fromTrade = toCanonicalUnixSec(candidate);
        if (fromTrade != null) return fromTrade;

        return Math.floor(Date.now() / 1000);
    }, [props?.executionUnixSec, props?.executions, tradeForMarkers]);

    // ALL entry/exit execution timestamps (epoch seconds UTC). Session detection
    // must use every execution, not just the trade date or the first fill.
    const executionTimesSec = useMemo(() => {
        const out: number[] = [];
        const push = (value: unknown) => {
            const sec = toCanonicalUnixSec(value);
            if (sec != null && Number.isFinite(sec) && sec > 0) out.push(sec);
        };

        const executionArrays = [
            Array.isArray(props?.executions) ? props.executions : [],
            Array.isArray(tradeForMarkers?.executions) ? tradeForMarkers.executions : [],
            Array.isArray(tradeForMarkers?.executionsList) ? tradeForMarkers.executionsList : [],
            Array.isArray(tradeForMarkers?.fills) ? tradeForMarkers.fills : [],
        ];
        for (const arr of executionArrays) {
            for (const exec of arr) {
                push(
                    exec?.timestamp
                    ?? exec?.time
                    ?? exec?.executedAt
                    ?? exec?.date
                    ?? exec?.t
                    ?? exec?.openTimestampMs
                    ?? exec?.closeTimestampMs
                    ?? exec?.entryTime
                    ?? exec?.exitTime
                    ?? exec?.openTime
                    ?? exec?.closeTime
                    ?? exec?.closedAt,
                );
            }
        }

        push(
            tradeForMarkers?.openTimestampMs
            ?? tradeForMarkers?.entryTimestampMs
            ?? tradeForMarkers?.openTime
            ?? tradeForMarkers?.entryTime
            ?? tradeForMarkers?.timestamp
            ?? tradeForMarkers?.date,
        );
        push(
            tradeForMarkers?.closeTimestampMs
            ?? tradeForMarkers?.exitTimestampMs
            ?? tradeForMarkers?.closeTime
            ?? tradeForMarkers?.exitTime
            ?? tradeForMarkers?.closedAt,
        );

        return [...new Set(out)].sort((a, b) => a - b);
    }, [props?.executions, tradeForMarkers]);

    const executionNyDates = useMemo(() => executionNyDateKeys(executionTimesSec), [executionTimesSec]);
    const isMultiDayExecution = executionNyDates.length > 1;
    const detectedSession = useMemo<TradeSessionStatus>(() => {
        const classified = detectTradeSession(executionTimesSec);
        // A trade spanning multiple NY dates is always MIXED SESSION.
        return isMultiDayExecution ? 'MIXED SESSION' : classified;
    }, [executionTimesSec, isMultiDayExecution]);

    const executionDateYmd = useMemo(() => {
        let dt = new Date(canonicalExecutionUnixSec * 1000);

        const postCloseSecByTf: Record<string, number> = {
            '1min': 100 * 60,
            '5min': 60 * 5 * 60,
            '15min': 50 * 15 * 60,
            '60min': 26 * 60 * 60,
            '1d': 10 * 24 * 60 * 60,
        };
        const requiredPostCloseSec = postCloseSecByTf[timeframe] ?? 0;

        if (secTypeUpper !== 'STK' && requiredPostCloseSec > 0) {
            const directCloseCandidates: unknown[] = [
                tradeForMarkers?.closeTimestampMs,
                tradeForMarkers?.exitTimestampMs,
                tradeForMarkers?.closeTimestamp,
                tradeForMarkers?.exitTimestamp,
                tradeForMarkers?.closeTime,
                tradeForMarkers?.exitTime,
                tradeForMarkers?.closeDate,
                tradeForMarkers?.exitDate,
                tradeForMarkers?.close_at,
                tradeForMarkers?.exit_at,
                tradeForMarkers?.closedAt,
            ];

            let closeUnixSec: number | null = null;
            for (const candidate of directCloseCandidates) {
                const parsed = toCanonicalUnixSec(candidate);
                if (parsed != null && (closeUnixSec == null || parsed > closeUnixSec)) {
                    closeUnixSec = parsed;
                }
            }

            const executionArrays: any[][] = [
                Array.isArray((props as any)?.executions) ? (props as any).executions : [],
                Array.isArray((tradeForMarkers as any)?.executions) ? (tradeForMarkers as any).executions : [],
                Array.isArray((tradeForMarkers as any)?.executionsList) ? (tradeForMarkers as any).executionsList : [],
                Array.isArray((tradeForMarkers as any)?.fills) ? (tradeForMarkers as any).fills : [],
            ];

            for (const arr of executionArrays) {
                for (const exec of arr) {
                    const parsed = toCanonicalUnixSec(
                        exec?.timestamp
                        ?? exec?.time
                        ?? exec?.executedAt
                        ?? exec?.date
                        ?? exec?.t
                        ?? exec?.closeTimestampMs
                        ?? exec?.exitTimestampMs
                        ?? exec?.closeTime
                        ?? exec?.exitTime
                        ?? exec?.closeDate
                        ?? exec?.exitDate
                        ?? exec?.closedAt
                    );
                    if (parsed != null && (closeUnixSec == null || parsed > closeUnixSec)) {
                        closeUnixSec = parsed;
                    }
                }
            }

            if (closeUnixSec != null) {
                const nowSec = Math.floor(Date.now() / 1000);
                if (closeUnixSec < nowSec) {
                    const target = new Date((closeUnixSec + requiredPostCloseSec) * 1000);
                    if (target.getTime() > dt.getTime()) dt = target;
                }
            }
        }

        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/New_York",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(dt);
        const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
        return `${map.year}-${map.month}-${map.day}`;
    }, [canonicalExecutionUnixSec, timeframe, secTypeUpper, tradeForMarkers]);

    // ET session window for the trade date, used for the request window, bar
    // filtering, and initial viewport framing.
    const sessionRangeSec = useMemo(
        () => getTradeSessionRangeSec(detectedSession, executionDateYmd),
        [detectedSession, executionDateYmd],
    );
    const sessionRangeKey = `${sessionRangeSec.startSec}:${sessionRangeSec.endSec}:${detectedSession}`;

    // Candle timestamps (floored to the current timeframe) that carry a trade
    // execution. These must never be dropped by the session bar filter, even if
    // an execution falls just outside the detected session window.
    const executionCandleTimes = useMemo(() => {
        const policyTf = TF_TO_POLICY[timeframe] ?? timeframe ?? '1m';
        const tfSec = policyTf === '5m' ? 300
            : policyTf === '15m' ? 900
            : policyTf === '30m' ? 1800
            : policyTf === '60m' ? 3600
            : policyTf === '1d' ? 86400
            : 60;
        const set = new Set<number>();
        for (const sec of executionTimesSec) {
            set.add(Math.floor(sec / tfSec) * tfSec);
        }
        return set;
    }, [executionTimesSec, timeframe]);

    const isIndexInstrumentChart = useMemo(
        () => isIndexOrCfdInstrument(symbol, (propTrade as any)?.instrumentType ?? (propTrade as any)?.instrument, propTrade as any),
        [symbol, propTrade],
    );

    const equityMarketDateStatus = useMemo(() => (
        secTypeUpper === 'STK' && !isIndexInstrumentChart
            ? getUsEquityMarketDateStatus(executionDateYmd)
            : { dateKey: executionDateYmd, valid: true, reason: null, holidayName: null, isFuture: false }
    ), [executionDateYmd, secTypeUpper, isIndexInstrumentChart]);

    const tradeWindowBounds = useMemo(() => {
        const trade = tradeForMarkers ?? null;

        const directOpenMs = normalizeTimestampToMs(
            trade?.openTimestampMs
            ?? trade?.entryTimestampMs
            ?? trade?.openTime
            ?? trade?.entryTime
            ?? trade?.timestamp
            ?? trade?.date
        );

        const directCloseMs = normalizeTimestampToMs(
            trade?.closeTimestampMs
            ?? trade?.exitTimestampMs
            ?? trade?.closeTime
            ?? trade?.exitTime
            ?? trade?.closeDate
            ?? trade?.exitDate
            ?? trade?.closedAt
        );

        let minExecMs: number | null = null;
        let maxExecMs: number | null = null;
        const executionArrays = [
            Array.isArray(propExecutions) ? propExecutions : [],
            Array.isArray((trade as any)?.executions) ? (trade as any).executions : [],
            Array.isArray((trade as any)?.executionsList) ? (trade as any).executionsList : [],
            Array.isArray((trade as any)?.fills) ? (trade as any).fills : [],
        ];

        for (const arr of executionArrays) {
            for (const exec of arr) {
                const tsMs = normalizeTimestampToMs(
                    exec?.timestamp
                    ?? exec?.time
                    ?? exec?.executedAt
                    ?? exec?.date
                    ?? exec?.t
                    ?? exec?.openTimestampMs
                    ?? exec?.closeTimestampMs
                    ?? exec?.entryTime
                    ?? exec?.exitTime
                    ?? exec?.openTime
                    ?? exec?.closeTime
                );
                if (tsMs == null || !Number.isFinite(tsMs)) continue;
                if (minExecMs == null || tsMs < minExecMs) minExecMs = tsMs;
                if (maxExecMs == null || tsMs > maxExecMs) maxExecMs = tsMs;
            }
        }

        const openMs = directOpenMs ?? minExecMs;
        if (openMs == null || !Number.isFinite(openMs)) return null;

        let closeMs = directCloseMs ?? maxExecMs ?? Date.now();
        if (!Number.isFinite(closeMs)) closeMs = Date.now();
        if (closeMs < openMs) closeMs = openMs;

        return { openMs, closeMs };
    }, [propExecutions, tradeForMarkers]);

    // Provider request mode driven by the detected session:
    //   REGULAR        -> useRTH=1 (prepost=false / omitted)
    //   PRE/POST/MIXED -> useRTH=0 (prepost=true, full extended session)
    const requestUseRth = useMemo(() => detectedSession === 'REGULAR', [detectedSession]);
    const requestContractMonth = useMemo(() => {
        const trade = propTrade || {};
        return String(
            trade?.lastTradeDateOrContractMonth
            || trade?.expiry
            || trade?.contractMonth
            || props?.lastTradeDateOrContractMonth
            || ''
        ).trim();
    }, [propTrade?.lastTradeDateOrContractMonth, propTrade?.expiry, propTrade?.contractMonth, props?.lastTradeDateOrContractMonth]);
    const requestLocalSymbol = useMemo(() => {
        const trade = propTrade || {};
        return String(trade?.localSymbol || props?.localSymbol || '').trim();
    }, [propTrade?.localSymbol, props?.localSymbol]);
    const requestConid = useMemo(() => {
        const trade = propTrade || {};
        return String(trade?.conid || trade?.conId || props?.conid || '').trim();
    }, [propTrade?.conid, propTrade?.conId, props?.conid]);

        useEffect(() => {
            return () => {
                if (toolWarningTimerRef.current) {
                    window.clearTimeout(toolWarningTimerRef.current);
                    toolWarningTimerRef.current = null;
                }
            };
        }, []);

    useEffect(() => {
        // Pass the policy timeframe so 1D bars (UTC-midnight convention) bypass intraday hour-of-day filtering.
        const policyTf = TF_TO_POLICY[timeframe] ?? timeframe ?? '1m';
        // Multi-day trades keep the complete requested window (rangeSec = null) so
        // users can scroll to every execution; single-day trades are trimmed to the
        // detected session window while preserving execution candles.
        const filtered = filterBarsBySession(
            rawData,
            isMultiDayExecution ? null : sessionRangeSec,
            secTypeUpper,
            policyTf,
            executionCandleTimes,
        );
        console.log('[TradeReplayChart][filterBarsBySession] rawData:', rawData.length, '→ filtered:', filtered.length,
            'session:', detectedSession, 'rangeSec:', JSON.stringify(sessionRangeSec),
            'multiDay:', isMultiDayExecution, 'tf:', policyTf, 'build:', DEV_BUILD_ID);
        setAllData(filtered);
        setIsPlaying(false);
        setIsReplayMode(false);
        setCurrentIndex(Math.max(0, filtered.length - 1));
        setDisplayedData(filtered);
        setHoverData(filtered.length > 0 ? filtered[filtered.length - 1] : null);
    }, [rawData, detectedSession, sessionRangeSec, isMultiDayExecution, executionCandleTimes, secTypeUpper, timeframe]);

    const tradeWindowsByPolicy = useMemo(() => {
        const result: Record<PolicyTf, ReturnType<typeof computeTradeWindow> | null> = {
            '1m': null,
            '5m': null,
            '15m': null,
            '30m': null,
            '60m': null,
            '1d': null,
        };

        if (!tradeWindowBounds) return result;

        for (const policyTf of TIMEFRAME_POLICY_ORDER) {
            result[policyTf] = computeTradeWindow({
                openMs: tradeWindowBounds.openMs,
                closeMs: tradeWindowBounds.closeMs,
                timeframe: policyTf,
            });
        }
        return result;
    }, [tradeWindowBounds]);

    const validTimeframes = useMemo(() => {
        const result = TIMEFRAME_POLICY_ORDER.reduce((acc, tf) => {
            if (!tradeWindowBounds) {
                acc[tf] = true;
            } else {
                acc[tf] = Boolean(tradeWindowsByPolicy[tf]?.enabled);
            }
            return acc;
        }, {} as Record<string, boolean>);

        if (!Object.values(result).some(Boolean)) result['1d'] = true;
        return result;
    }, [tradeWindowBounds, tradeWindowsByPolicy]);

    const currentPolicyTf = TF_TO_POLICY[timeframe] ?? '1d';
    const activeTradeWindow = tradeWindowsByPolicy[currentPolicyTf];
    // The BOT/ATC context window bounds the data request to open-BOT .. close+ATC.
    // The initial viewport frames to the complete detected session of the trade
    // date, so the requested window must also cover that session — otherwise the
    // server clips the afternoon bars away and e.g. a 1m Full/Mixed session still
    // only shows the morning (9:30→12).
    const sessionCoveringWindow = useMemo(() => {
        if (!activeTradeWindow?.enabled) return null;
        return expandTradeWindowToSession(activeTradeWindow, sessionRangeSec);
    }, [activeTradeWindow, sessionRangeSec]);
    const activeTradeWindowKey = useMemo(() => {
        if (!activeTradeWindow?.enabled) return 'disabled';
        return [
            activeTradeWindow.bucketedStartSec,
            activeTradeWindow.bucketedEndSec,
            activeTradeWindow.openBucketSec,
            activeTradeWindow.closeBucketSec,
        ].join(':');
    }, [
        activeTradeWindow?.enabled,
        activeTradeWindow?.bucketedStartSec,
        activeTradeWindow?.bucketedEndSec,
        activeTradeWindow?.openBucketSec,
        activeTradeWindow?.closeBucketSec,
    ]);
    const shouldDeferFetchForTimeframeAdjustment = Boolean(tradeWindowBounds) && !validTimeframes[currentPolicyTf];

    useEffect(() => {
        if (!tradeWindowBounds) return;
        if (validTimeframes[currentPolicyTf]) return;

        const nextPolicy = pickNextEnabledPolicy(currentPolicyTf, validTimeframes);
        const nextTf = POLICY_TO_TF[nextPolicy];
        if (nextTf !== timeframe) {
            setTimeframe(nextTf);
            setTimeframeAdjustedMsg('Trade duration too long for this timeframe.');
            if (timeframeMsgTimerRef.current) {
                window.clearTimeout(timeframeMsgTimerRef.current);
                timeframeMsgTimerRef.current = null;
            }
            timeframeMsgTimerRef.current = window.setTimeout(() => {
                setTimeframeAdjustedMsg('');
                timeframeMsgTimerRef.current = null;
            }, 3500);
        }
    }, [tradeWindowBounds, validTimeframes, currentPolicyTf, timeframe]);

    useEffect(() => {
        return () => {
            if (timeframeMsgTimerRef.current) {
                window.clearTimeout(timeframeMsgTimerRef.current);
                timeframeMsgTimerRef.current = null;
            }
        };
    }, []);

  useEffect(() => {
      drawModeRef.current = drawMode;
      drawingsLockedRef.current = drawingsLocked;
      draggedLineRef.current = draggedLine;
      notesRef.current = notes;
  }, [drawMode, drawingsLocked, draggedLine, notes]);

  useEffect(() => {
      symbolDrawingsRef.current = symbolDrawings;
  }, [symbolDrawings]);

  useEffect(() => {
      tradeDrawingsRef.current = tradeDrawings;
  }, [tradeDrawings]);

  useEffect(() => {
      if (activeTradeId) {
          setNewDrawingScope('trade');
          return;
      }
      setNewDrawingScope('symbol');
  }, [activeTradeId]);

  useEffect(() => {
      setChartResolution((prev) => {
          if (prev.requestedSymbol === defaultChartResolution.requestedSymbol && prev.assetClass === defaultChartResolution.assetClass) {
              return prev;
          }
          return defaultChartResolution;
      });
  }, [defaultChartResolution]);

  useEffect(() => {
            markersEnabledRef.current = tradeMarkersVisible;
    }, [tradeMarkersVisible]);

  useEffect(() => {
      const allHidden = !symbolDrawings && !showTradeDrawings;
      setDrawingsHidden(allHidden);
  }, [symbolDrawings, showTradeDrawings]);

    // Disable chart scroll/pan interactions when scrolling is locked
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      chart.applyOptions({
                handleScroll: {
                    mouseWheel: !scrollLocked,
                    pressedMouseMove: !scrollLocked,
                    horzTouchDrag: !scrollLocked,
                    vertTouchDrag: !scrollLocked,
                },
                handleScale: { mouseWheel: true },
      });
    } catch {}

  }, [scrollLocked]);

  const allPersistedDrawings = useMemo(() => [...symbolDrawings, ...tradeDrawings], [symbolDrawings, tradeDrawings]);

  const upsertDrawingLocal = useCallback((drawing: Drawing) => {
      if (drawing.scope === 'trade') {
          setTradeDrawings((prev) => {
              const idx = prev.findIndex((d) => d.id === drawing.id);
              if (idx < 0) return [...prev, drawing];
              const next = [...prev];
              next[idx] = drawing;
              return next;
          });
          return;
      }

      setSymbolDrawings((prev) => {
          const idx = prev.findIndex((d) => d.id === drawing.id);
          if (idx < 0) return [...prev, drawing];
          const next = [...prev];
          next[idx] = drawing;
          return next;
      });
  }, []);

  const removeDrawingLocal = useCallback((id: string) => {
      setSymbolDrawings((prev) => prev.filter((d) => d.id !== id));
      setTradeDrawings((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const persistCreateOrUpdateDrawing = useCallback(async (drawing: Drawing) => {
      const prevSymbol = symbolDrawingsRef.current;
      const prevTrade = tradeDrawingsRef.current;

      upsertDrawingLocal(drawing);
      try {
          const saved = await upsertDrawing(drawing);
          upsertDrawingLocal(saved);
      } catch (error) {
          setSymbolDrawings(prevSymbol);
          setTradeDrawings(prevTrade);
          setLoadMsg(`Drawings save failed: ${String((error as any)?.message || error)}`);
      }
  }, [upsertDrawingLocal]);

  const persistDeleteDrawing = useCallback(async (drawingId: string) => {
      const prevSymbol = symbolDrawingsRef.current;
      const prevTrade = tradeDrawingsRef.current;
      removeDrawingLocal(drawingId);

      try {
          await deleteDrawing(drawingId);
      } catch (error) {
          setSymbolDrawings(prevSymbol);
          setTradeDrawings(prevTrade);
          setLoadMsg(`Drawings delete failed: ${String((error as any)?.message || error)}`);
      }
  }, [removeDrawingLocal]);

  useEffect(() => {
      if (!symbol) {
          setSymbolDrawings([]);
          setTradeDrawings([]);
          return;
      }

      let cancelled = false;
      (async () => {
          try {
              const payload = await getDrawings(symbol, activeTradeId);
              if (cancelled) return;
              setSymbolDrawings(payload.symbolDrawings || []);
              setTradeDrawings(payload.tradeDrawings || []);
          } catch (error) {
              if (cancelled) return;
              setLoadMsg(`Drawings load failed: ${String((error as any)?.message || error)}`);
          }
      })();

      return () => {
          cancelled = true;
      };
  }, [symbol, activeTradeId]);



  // --- DATA FETCH ---
    useEffect(() => {
    if (shouldDeferFetchForTimeframeAdjustment) return;
    if (!symbol) return;
        const requestTimeframe = TF_TO_POLICY[timeframe] === '60m'
            ? '1h'
            : ((TF_TO_POLICY[timeframe] ?? '1m') as any);
        if (!equityMarketDateStatus.valid) {
            setIsPlaying(false);
            setLatestMeta(null);
            setRawData([]);
            setAllData([]);
            setDisplayedData([]);
            setChartResolution({
                ...defaultChartResolution,
                requestedSymbol: symbol,
                resolvedSymbol: symbol,
                requestedTimeframe: requestTimeframe,
                resolvedTimeframe: requestTimeframe,
                chartMode: 'unavailable',
                barsAvailable: false,
                replayAllowed: false,
                metricsAllowed: false,
                sourceType: 'none',
                fallbackReason: 'invalid_equity_market_date',
                warningCode: 'INVALID_EQUITY_MARKET_DATE',
                warningMessage: equityMarketDateStatus.reason,
            });
            setLoadMsg(equityMarketDateStatus.reason || 'The U.S. equity market was closed on this trade date.');
            setIsLoading(false);
            abortRef.current?.abort();
            inFlightRequestKeyRef.current = null;
            dataVersionRef.current += 1;
            return;
        }
        const requestKey = JSON.stringify({
                symbol,
                secType,
                propExchange: propExchange || '',
                propCurrency: propCurrency || '',
                timeframe,
                executionDateYmd,
                requestUseRth,
                sessionRangeKey,
                activeTradeWindowKey,
                requestContractMonth,
                requestLocalSymbol,
                requestConid,
                activeTradeId: activeTradeId || '',
        });
        if (inFlightRequestKeyRef.current === requestKey) return;
    setIsPlaying(false);
    setLatestMeta(null);
    setChartResolution({
        ...defaultChartResolution,
        requestedSymbol: symbol,
        resolvedSymbol: symbol,
        requestedTimeframe: TF_TO_POLICY[timeframe] === '60m' ? '1h' : ((TF_TO_POLICY[timeframe] ?? '1m') as any),
        resolvedTimeframe: TF_TO_POLICY[timeframe] === '60m' ? '1h' : ((TF_TO_POLICY[timeframe] ?? '1m') as any),
    });
    if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
        inFlightRequestKeyRef.current = requestKey;
        dataVersionRef.current += 1;
        const fetchVersion = dataVersionRef.current;

        const mkQs = (attempt?: TradeChartFetchAttempt) => {
      const qs = new URLSearchParams();
            qs.set("symbol", String(attempt?.symbol || symbol));
      qs.set("secType", secType);
            qs.set("timeframe", toRequestTimeframe((attempt?.timeframe ?? ((TF_TO_POLICY[timeframe] === '60m' ? '1h' : (TF_TO_POLICY[timeframe] ?? '1m')) as any))));
      qs.set("date", executionDateYmd);
            // Tag this request origin so backend logs can distinguish replay-chart usage
            qs.set("caller", "replayChart");
            // If this request is for the context-only Daily timeframe, mark it
            // explicitly so the backend can skip replay-usage accounting. All
            // intraday replay timeframes (1m/5m/15m/30m/1h) are usage-eligible.
            const policyTf = (attempt?.timeframe ?? (TF_TO_POLICY[timeframe] === '60m' ? '1h' : (TF_TO_POLICY[timeframe] ?? '1m')) as any);
            if (policyTf === '1d') {
                qs.set('replayUsageEligible', '0');
            }
            // Only include a tradeId when it's a real value (avoid literal strings like "undefined"/"null")
            const normalizeTradeIdForQuery = (v: unknown) => {
                if (v == null) return null;
                const s = String(v || '').trim();
                if (!s) return null;
                const low = s.toLowerCase();
                if (low === 'undefined' || low === 'null') return null;
                return s;
            };
            const normalizedTradeId = normalizeTradeIdForQuery(activeTradeId);
            if (normalizedTradeId) qs.set("tradeId", normalizedTradeId);

            if (activeTradeWindow?.enabled) {
                const requestWindow = sessionCoveringWindow ?? activeTradeWindow;
                qs.set("start", String(requestWindow.bucketedStartSec));
                qs.set("end", String(requestWindow.bucketedEndSec));
                qs.set("startSec", String(requestWindow.bucketedStartSec));
                qs.set("endSec", String(requestWindow.bucketedEndSec));
                qs.set("openBucketSec", String(requestWindow.openBucketSec));
                qs.set("closeBucketSec", String(requestWindow.closeBucketSec));
            }

    qs.set("useRTH", requestUseRth ? "1" : "0");
      if (propExchange) qs.set("exchange", propExchange);
      if (propCurrency) qs.set("currency", propCurrency);

      // include contract-specific qualifiers when provided by parent (TradeDetails)
            const expiry = attempt?.qualifiers?.lastTradeDateOrContractMonth
                || requestContractMonth
                || undefined;
      if (expiry) qs.set('lastTradeDateOrContractMonth', String(expiry));
            const localSym = attempt?.qualifiers?.localSymbol || requestLocalSymbol || undefined;
      if (localSym) qs.set('localSymbol', String(localSym));
            const conid = attempt?.qualifiers?.conid || requestConid || undefined;
      if (conid) qs.set('conid', String(conid));

      return qs;
    };

    const buildUrl = (base: string, qs: URLSearchParams) => {
      const path = `/api/market/ohlc?${qs.toString()}`;
      return base ? `${base}${path}` : path;
    };

        const shouldTryAlternateBase = (payload: any) => {
            const status = Number(payload?.status);
            if (Number.isFinite(status) && status >= 200 && status < 300) return false;
            if (payload?.error !== true) return false;
            if (status === 429) return false;
            return true;
        };

        const resolveAlternateBase = async (failedBase: string) => {
            if (chosenBaseRef.current && chosenBaseRef.current !== failedBase) return chosenBaseRef.current;
            if (!baseProbePromiseRef.current) {
                baseProbePromiseRef.current = (async () => {
                    const candidates = buildBaseCandidates().filter((base) => base !== failedBase);
                    for (const base of candidates) {
                        try {
                            const probe = await fetchJsonWithTimeout(`${base}/api/market/time`, ac.signal, 1500);
                            if (probe && probe.nowIso) return base;
                        } catch {}
                    }
                    return null;
                })().finally(() => {
                    baseProbePromiseRef.current = null;
                });
            }

            const resolved = await baseProbePromiseRef.current;
            if (resolved) chosenBaseRef.current = resolved;
            return resolved;
        };

        const fetchOhlc = async (attempt?: TradeChartFetchAttempt): Promise<any> => {
            const bases = chosenBaseRef.current ? [chosenBaseRef.current] : buildBaseCandidates();
            const primaryBase = bases[0] || '';
            let requestInit: RequestInit | undefined;
            try {
                const session = await getSessionForAuthenticatedRequest({ reason: 'replay_chart_ohlc' });
                const accessToken = String(session?.access_token || '').trim();
                if (accessToken) requestInit = { headers: { Authorization: `Bearer ${accessToken}` } };
            } catch {
                requestInit = undefined;
            }

            const primaryPayload = await fetchJsonWithTimeout(buildUrl(primaryBase, mkQs(attempt)), ac.signal, 25000, requestInit);

            if (primaryPayload?.status && primaryPayload.status >= 200 && primaryPayload.status < 300) {
                return primaryPayload;
            }

            if (!shouldTryAlternateBase(primaryPayload)) return primaryPayload;

            const alternateBase = await resolveAlternateBase(primaryBase);
            if (!alternateBase || alternateBase === primaryBase) return primaryPayload;

            const fallbackPayload = await fetchJsonWithTimeout(buildUrl(alternateBase, mkQs(attempt)), ac.signal, 25000, requestInit);
            if (fallbackPayload?.status && fallbackPayload.status >= 200 && fallbackPayload.status < 300) {
                chosenBaseRef.current = alternateBase;
            }
            return fallbackPayload;
        };

    const run = async () => {
      try {
                setIsLoading(true);
        setLoadMsg("");
            setRawData([]);
                const exactContract = {
                    localSymbol: requestLocalSymbol || null,
                    lastTradeDateOrContractMonth: requestContractMonth || null,
                    conid: requestConid || null,
                };

                const loadResult = await resolveTradeChartSource({
                    assetClass: secType,
                    requestedSymbol: symbol,
                    requestedTimeframe: TF_TO_POLICY[timeframe] === '60m' ? '1h' : (TF_TO_POLICY[timeframe] ?? '1m'),
                    instrumentType: (propTrade as any)?.instrumentType ?? (propTrade as any)?.instrument ?? null,
                    exactContract,
                    fetchBars: async (attempt) => {
                        const payload = await fetchOhlc(attempt);
                        return {
                            payload,
                            candles: normalizeBars(extractRawBars(payload)),
                        };
                    },
                });
                if (ac.signal.aborted || fetchVersion !== dataVersionRef.current) return;

                setChartResolution(loadResult.resolution);
                try {
                    setLatestMeta(extractTradeChartMeta(loadResult.payload, loadResult.resolution));
                } catch {}

                const timeframeChanged = prevTimeframeRef.current !== timeframe;
                prevTimeframeRef.current = timeframe;

                const candles = getResolvedCandles(loadResult.resolution, loadResult.candles);
                const resolutionMs = ({ '1m': 60_000, '5m': 300_000, '15m': 900_000, '1h': 3_600_000, '1d': 86_400_000 } as Record<string, number>)[loadResult.resolution.resolvedTimeframe] ?? null;
                const priceExecutions = (Array.isArray(propExecutions) ? propExecutions : []).map((execution: any) => {
                    const timestampSec = toCanonicalUnixSec(execution?.timestamp ?? execution?.time);
                    return {
                        timestamp: timestampSec == null ? NaN : timestampSec * 1000,
                        side: String(execution?.side ?? '').toLowerCase() === 'sell' ? 'sell' as const : 'buy' as const,
                        qty: Math.abs(Number(execution?.qty ?? execution?.quantity ?? execution?.volume)),
                        price: Number(execution?.price),
                    };
                }).filter((execution: any) => Number.isFinite(execution.timestamp) && Number.isFinite(execution.qty) && execution.qty > 0 && Number.isFinite(execution.price));
                const pricesMismatch = !isDemoTradeActive && priceExecutions.length >= 2 && loadResult.resolution.metricsAllowed
                    && !hasCoherentTradeInsightBars({
                        executions: priceExecutions,
                        bars: candles.map((bar: any) => ({
                            time: Number(bar.time) > 1_000_000_000_000 ? Number(bar.time) : Number(bar.time) * 1000,
                            high: Number(bar.high ?? bar.h),
                            low: Number(bar.low ?? bar.l),
                        })),
                        marketBarResolutionMs: resolutionMs,
                    });
                if (pricesMismatch) {
                    if (import.meta.env?.DEV) {
                        console.warn('[TradeReplayChart] Historical bar prices differ from execution prices; suppressing excursion metrics while preserving chart and replay.');
                    }
                    setChartResolution({
                        ...loadResult.resolution,
                        metricsAllowed: false,
                        warningCode: 'EXECUTION_PRICE_MISMATCH',
                        warningMessage: null,
                    });
                } else {
                    setChartResolution(loadResult.resolution);
                }
                if (candles.length > 0) {
                    if (timeframeChanged) {
                        pendingExecutionAutoFocusRef.current = true;
                    }
                    console.log('[TradeReplayChart][rawData]', candles.length, 'candles received. useRTH:', requestUseRth, 'tf:', timeframe, 'build:', DEV_BUILD_ID);
                    setRawData(candles);
                    setLoadMsg('');
                } else {
                    setRawData([]);
                    setLoadMsg(loadResult.payload?.message ? String(loadResult.payload.message) : 'No candles returned.');
                }
      } catch (e: any) {
         if (e?.name !== "AbortError") {
             if (Number(e?.status) === 429) {
                 const waitMs = Number.isFinite(Number(e?.retryAfterMs)) && Number(e.retryAfterMs) > 0 ? Number(e.retryAfterMs) : 1500;
                 setLoadMsg(`Cooldown… retrying in ${Math.max(1, Math.ceil(waitMs / 1000))}s`);
                 retryTimerRef.current = window.setTimeout(() => {
                     if (!ac.signal.aborted) run();
                 }, waitMs);
                 return;
             }
             setRawData([]);
             setLatestMeta(null);
             setChartResolution((prev) => ({
                ...prev,
                chartMode: 'unavailable',
                barsAvailable: false,
                replayAllowed: false,
                metricsAllowed: false,
                sourceType: 'none',
                warningCode: 'LOAD_ERROR',
                warningMessage: 'Historical chart load failed.',
             }));
             setLoadMsg("Fetch failed.");
         }
      } finally {
                if (inFlightRequestKeyRef.current === requestKey) {
                    inFlightRequestKeyRef.current = null;
                }
                if (!ac.signal.aborted && fetchVersion === dataVersionRef.current) {
                    setIsLoading(false);
                }
      }
    };
    run();
    return () => {
        ac.abort();
        if (inFlightRequestKeyRef.current === requestKey) {
            inFlightRequestKeyRef.current = null;
        }
        if (retryTimerRef.current) {
            window.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    }
    }, [symbol, secType, propExchange, propCurrency, timeframe, executionDateYmd, requestUseRth, sessionRangeKey, shouldDeferFetchForTimeframeAdjustment, activeTradeWindowKey, requestContractMonth, requestLocalSymbol, requestConid, activeTradeId, equityMarketDateStatus.valid, equityMarketDateStatus.reason, propExecutions, isDemoTradeActive]);

    // --- CHART INIT ---
    useEffect(() => {
    if (!containerRef.current) return;
        if (chartRef.current) return; // already initialized

        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width < 10 || rect.height < 10) {
                setChartInitError("Chart container has zero size. Resize the window or retry.");
                const retry = window.setTimeout(() => {
                    try { setChartInitNonce((n) => n + 1); } catch {}
                }, 300);
                return () => {
                    try { window.clearTimeout(retry); } catch {}
                };
        }
        let chart: IChartApi | null = null;
        try {
            chart = createChart(containerRef.current, {
        layout: { background: { type: ColorType.Solid, color: dark ? "#1a1a1c" : "#ffffff" }, textColor: dark ? "#d1d4dc" : "#334155" },
        grid: { vertLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" }, horzLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" } },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
            localization: {
                timeFormatter: (time: any) => {
                    const sec = toUnixSecForChartTime(time);
                    return sec == null ? '' : formatChartTime(sec, tzMode, true, displayTimeZoneRef.current);
                },
            },
            handleScroll: {
                mouseWheel: !scrollLocked,
                pressedMouseMove: !scrollLocked,
                horzTouchDrag: !scrollLocked,
                vertTouchDrag: !scrollLocked,
            },
            handleScale: { mouseWheel: true },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: { color: '#A020F0', width: 1, style: 0, labelBackgroundColor: '#A020F0' },
                horzLine: { color: '#A020F0', width: 1, style: 0, labelBackgroundColor: '#A020F0' },
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false,
                borderColor: dark ? "rgba(197, 203, 206, 0.4)" : "rgba(203, 213, 225, 0.9)",
                tickMarkFormatter: (time: any) => {
                    const sec = toUnixSecForChartTime(time);
                    return sec == null ? '' : formatChartTime(sec, tzMode, false, displayTimeZoneRef.current);
                },
            },
            rightPriceScale: {
                visible: true,
                autoScale: true,
                mode: 0,
                entireTextOnly: true,
                borderColor: dark ? "rgba(197, 203, 206, 0.4)" : "rgba(203, 213, 225, 0.9)",
                scaleMargins: { top: 0.08, bottom: 0.22 },
            },
            leftPriceScale: {
                visible: false,
            },
            });
        } catch (e: any) {
            setChartInitError(String(e?.message ?? e));
            return;
        }

    // Use minimal options first to avoid triggering internal assertions in some builds
        const candlePriceFormat = detectPriceFormat(symbol, secType);

        const candleSeries = chart
            ? safeAddCandlestickSeries(chart, {
                    priceScaleId: 'right',
                    priceFormat: {
                        type: 'price',
                        precision: candlePriceFormat.precision,
                        minMove: candlePriceFormat.minMove,
                    },
                })
            : null;

    console.debug('[TradeReplayChart] chart created', { chart });
    console.debug('[TradeReplayChart] series', { candleSeries });

    // If the candlestick series failed to be created, surface a clear UI fallback
    // and avoid leaving a partially-initialized chart instance mounted.
    if (!candleSeries) {
        console.error('[TradeReplayChart] failed to create candlestick series; series API incompatible?', { chart, candleSeries });
        try { setLoadMsg('Chart initialization failed (see console)'); } catch {}
        try { chart.remove(); } catch { /* ignore */ }
        setChartInitError('Failed to create candlestick series; check console for details.');
        return;
    }

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    setChartInitError(null);

    try {
        volumeSeriesRef.current = addVolumeSeries(chart);
    } catch (e) {
        console.warn("[TradeReplayChart] failed to create volume series", e);
    }

    drawingManagerRef.current = new DrawingManager({
        chart,
        candleSeries,
        container: containerRef.current,
        symbol,
        tradeId: activeTradeId,
        scopeForNew: newDrawingScope,
        visibility: { symbol: true, trade: showTradeDrawings },
        callbacks: {
            onCreate: (drawing) => {
                persistCreateOrUpdateDrawing(drawing);
                setDrawMode('select');
            },
            onUpdate: (drawing) => {
                persistCreateOrUpdateDrawing(drawing);
            },
            onDelete: (drawingId) => {
                persistDeleteDrawing(drawingId);
            },
            onInvalidPlacement: (message) => {
                setToolWarningMsg(String(message || 'Tool cannot be used outside candle area.'));
                if (toolWarningTimerRef.current) {
                    window.clearTimeout(toolWarningTimerRef.current);
                }
                toolWarningTimerRef.current = window.setTimeout(() => {
                    setToolWarningMsg('');
                    toolWarningTimerRef.current = null;
                }, 1800);
            },
        },
    });
    drawingManagerRef.current.setDrawings(allPersistedDrawings);
    syncDrawingManagerBarTimes(drawingManagerRef.current as any, processedData as any);



    // Click Handler
    const clearFocusedExecution = () => {
        try {
            if (focusPulseTimerRef.current != null) {
                window.clearTimeout(focusPulseTimerRef.current as any);
                focusPulseTimerRef.current = null;
            }
        } catch {}
        try { setFocusedExecutionId(null); } catch {}
    };

    chart.subscribeClick(() => {
        // allow clicking to clear the focused execution marker
        try {
            if (drawingsLockedRef.current) return;
        } catch {}
        clearFocusedExecution();
    });

    // Also listen for pointerdown on the container so clicks anywhere inside the chart area clear focus
    const containerEl = containerRef.current;
    const onPointerDown = (ev: PointerEvent) => {
        // ignore if user is resizing or dragging UI controls
        try {
            if ((ev as any)?.button === 2) return; // ignore right-click
        } catch {}
        clearFocusedExecution();
    };
    try { containerEl?.addEventListener('pointerdown', onPointerDown); } catch {}

    // Crosshair
    chart.subscribeCrosshairMove((param: any) => {
        if (param.time) {
            try {
                const data = param.seriesData.get(candleSeries);
                const t = typeof param.time === "number" ? param.time : Number(param.time);
                if (data) {
                    const volumeFromMap = Number.isFinite(t) ? volumeByTimeRef.current.get(t) : undefined;
                    const volumeFromData = Number.isFinite(Number((data as any)?.volume)) ? Number((data as any)?.volume) : undefined;
                    const volume = volumeFromData ?? volumeFromMap;
                    setHoverData({ ...data, volume });
                } else if (Number.isFinite(t)) {
                    // fallback: look up canonical candle by time
                    const candle = candleByTimeRef.current.get(t);
                    const vol = volumeByTimeRef.current.get(t);
                    if (candle) setHoverData({ open: candle.open, high: candle.high, low: candle.low, close: candle.close, time: candle.time, volume: vol });
                }

                if (markersEnabledRef.current && Number.isFinite(t)) {
                    const toleranceSec = markerToleranceSecRef.current;
                    const nearestMarker = visibleMarkersRef.current.find((marker) => Math.abs(marker.time - t) <= toleranceSec);

                    if (nearestMarker) {
                        if (markerHoverIdRef.current !== nearestMarker.id) {
                            markerHoverIdRef.current = nearestMarker.id;
                            setHoveredTradeMarker(nearestMarker);
                        }
                    } else if (markerHoverIdRef.current !== null) {
                        markerHoverIdRef.current = null;
                        setHoveredTradeMarker(null);
                    }
                } else if (markerHoverIdRef.current !== null) {
                    markerHoverIdRef.current = null;
                    setHoveredTradeMarker(null);
                }
            } catch {}
        } else if (markerHoverIdRef.current !== null) {
            markerHoverIdRef.current = null;
            setHoveredTradeMarker(null);
        }

        if (draggedLineRef.current && param.point) {
            const newPrice = candleSeries.coordinateToPrice(param.point.y);
            if(newPrice != null) {
                try { draggedLineRef.current.applyOptions({ price: newPrice }); } catch {}
            }
        }
    });

    let resizeRaf: number | null = null;
    let lastWidth = -1;
    let lastHeight = -1;
    const resizeObserver = new ResizeObserver(entries => {
        if (entries.length === 0 || !entries[0].target) return;
        const newRect = entries[0].contentRect;
        const width = Math.round(Number(newRect.width) || 0);
        const height = Math.round(Number(newRect.height) || 0);
        if (width <= 0 || height <= 0) return;
        if (width === lastWidth && height === lastHeight) return;
        lastWidth = width;
        lastHeight = height;
        if (resizeRaf != null) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => {
            resizeRaf = null;
            try { chart?.applyOptions({ width, height }); } catch {}
        });
    });
    resizeObserver.observe(containerRef.current);

    return () => {
        try { drawingManagerRef.current?.destroy(); } catch {}
        drawingManagerRef.current = null;
        try { resizeObserver.disconnect(); } catch {}
        if (resizeRaf != null) {
            try { cancelAnimationFrame(resizeRaf); } catch {}
            resizeRaf = null;
        }
                try { chart?.remove(); } catch {}
        chartRef.current = null;
        candleSeriesRef.current = null;
        lineSeriesRefs.current.clear();
        try { containerEl?.removeEventListener('pointerdown', onPointerDown); } catch {}
    };
    }, [chartInitNonce]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        try {
            chart.applyOptions({
                layout: { background: { type: ColorType.Solid, color: dark ? "#1a1a1c" : "#ffffff" }, textColor: dark ? "#d1d4dc" : "#334155" },
                grid: {
                    vertLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" },
                    horzLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" },
                },
                timeScale: { borderColor: dark ? "rgba(197, 203, 206, 0.4)" : "rgba(203, 213, 225, 0.9)" },
                rightPriceScale: { borderColor: dark ? "rgba(197, 203, 206, 0.4)" : "rgba(203, 213, 225, 0.9)" },
            });
        } catch {}
    }, [dark]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        try {
            chart.applyOptions({
                localization: {
                    timeFormatter: (time: any) => {
                        const sec = toUnixSecForChartTime(time);
                        return sec == null ? '' : formatChartTime(sec, tzMode, true, displayTimeZone);
                    },
                },
                timeScale: {
                    tickMarkFormatter: (time: any) => {
                        const sec = toUnixSecForChartTime(time);
                        return sec == null ? '' : formatChartTime(sec, tzMode, false, displayTimeZone);
                    },
                },
            });
        } catch {}
    }, [tzMode, displayTimeZone]);

    useEffect(() => {
        drawingManagerRef.current?.setMode(drawMode);
    }, [drawMode]);

    useEffect(() => {
        const manager = drawingManagerRef.current;
        if (!manager) return;
        manager.setContext(symbol, activeTradeId);
        manager.setScopeForNew(newDrawingScope);
        manager.setVisibility({ symbol: true, trade: showTradeDrawings });
        manager.setLocked(drawingsLocked);
    }, [symbol, activeTradeId, newDrawingScope, symbolDrawings, showTradeDrawings, drawingsLocked]);

    useEffect(() => {
        drawingManagerRef.current?.setDrawings(allPersistedDrawings);
    }, [allPersistedDrawings]);

    useEffect(() => {
        drawingManagerRef.current?.setDrawings(allPersistedDrawings);
    }, [allPersistedDrawings, timeframe, displayedData.length, detectedSession, tzMode, displayTimeZone]);

    // Auto-retry chart init once data arrives (in case init ran before layout sizing)
    useEffect(() => {
        if (chartRef.current) return;
        if (!containerRef.current) return;
        if (displayedData.length === 0) return;
        setChartInitNonce((n) => n + 1);
    }, [displayedData.length]);



  // --- MACD CHART INIT ---
  useEffect(() => {
    if (!showMACD || !macdContainerRef.current) return;
    const chart = createChart(macdContainerRef.current, {
                layout: { background: { type: ColorType.Solid, color: dark ? "#131722" : "#ffffff" }, textColor: dark ? "#d1d4dc" : "#334155" },
                grid: { vertLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" }, horzLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" } },
        width: macdContainerRef.current.clientWidth,
        height: macdContainerRef.current.clientHeight,
        timeScale: { visible: false },
                crosshair: {
                    mode: CrosshairMode.Normal,
                    vertLine: { color: '#A020F0', width: 1, style: 0, labelBackgroundColor: '#A020F0' },
                    horzLine: { color: '#A020F0', width: 1, style: 0, labelBackgroundColor: '#A020F0' },
                },
    });

    const macdSeries = safeAddLineSeries(chart, { color: '#2962FF', lineWidth: 2, crosshairMarkerVisible: false });
    const signalSeries = safeAddLineSeries(chart, { color: '#FF6D00', lineWidth: 2, crosshairMarkerVisible: false });
    const histogramSeries = safeAddHistogramSeries(chart, { color: DEFAULT_HIST_COLOR, baseValue: 0 });

    macdChartRef.current = chart;
    macdSeriesRefs.current = { macd: macdSeries, signal: signalSeries, histogram: histogramSeries };

    const syncHandler = (range: any) => {
        if (range && macdChartRef.current) {
            try { macdChartRef.current.timeScale().setVisibleRange(range); } catch {}
        }
    };

    if (chartRef.current) {
        try {
            chartRef.current.timeScale().subscribeVisibleTimeRangeChange(syncHandler);
            const currentRange = chartRef.current.timeScale().getVisibleRange();
            if(currentRange) chart.timeScale().setVisibleRange(currentRange);
        } catch {}
    }

    let resizeRaf: number | null = null;
    let lastWidth = -1;
    let lastHeight = -1;
    const resizeObserver = new ResizeObserver(entries => {
        if (entries.length === 0 || !entries[0].target) return;
        const newRect = entries[0].contentRect;
        const width = Math.round(Number(newRect.width) || 0);
        const height = Math.round(Number(newRect.height) || 0);
        if (width <= 0 || height <= 0) return;
        if (width === lastWidth && height === lastHeight) return;
        lastWidth = width;
        lastHeight = height;
        if (resizeRaf != null) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => {
            resizeRaf = null;
            try { chart.applyOptions({ width, height }); } catch {}
        });
    });
    resizeObserver.observe(macdContainerRef.current);

    return () => {
        try { resizeObserver.disconnect(); } catch {}
        if (resizeRaf != null) {
            try { cancelAnimationFrame(resizeRaf); } catch {}
            resizeRaf = null;
        }
        if (chartRef.current) {
            try { chartRef.current.timeScale().unsubscribeVisibleTimeRangeChange(syncHandler); } catch {}
        }
        try { chart.remove(); } catch {}
        macdChartRef.current = null;
        macdSeriesRefs.current = {};
    };
  }, [showMACD]);

  useEffect(() => {
    const chart = macdChartRef.current;
    if (!chart) return;
    try {
      chart.applyOptions({
        layout: { background: { type: ColorType.Solid, color: dark ? "#131722" : "#ffffff" }, textColor: dark ? "#d1d4dc" : "#334155" },
        grid: {
          vertLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" },
          horzLines: { color: dark ? "rgba(42, 46, 57, 0.2)" : "rgba(226, 232, 240, 0.9)" },
        },
      });
    } catch {}
  }, [dark]);

  // --- MAIN RENDER LOOP ---
  const processedData = useMemo(() => {
    return candleMode === "heikin" ? toHeikinAshi(displayedData) : displayedData;
  }, [displayedData, candleMode]);
        const didFilterBars = useMemo(
                () => rawData.length > 0 && displayedData.length !== rawData.length,
                [rawData.length, displayedData.length],
        );
        // Execution markers are always shown: the session bar filter preserves
        // execution candles, so markers are never hidden by the session view.
    const allMarkers = useMemo(() => {
        const fromTrade = tradeForMarkers ? buildTradeMarkers(tradeForMarkers) : [];
        if (fromTrade.length > 0) return fromTrade;

        const externalExecutions = Array.isArray(props?.executions) ? props.executions : [];
        if (externalExecutions.length === 0) return fromTrade;

        return buildTradeMarkers({
            ...(tradeForMarkers || {}),
            executions: externalExecutions,
        });
    }, [tradeForMarkers, props?.executions]);
    const tradeExecutions = useMemo(() => {
        const fromTrade = tradeForMarkers ? toTradeExecutions(tradeForMarkers) : [];
        if (fromTrade.length > 0) return fromTrade;

        const externalExecutions = Array.isArray(props?.executions) ? props.executions : [];
        if (externalExecutions.length === 0) return [] as TradeExecution[];

        return toTradeExecutions({
            ...(tradeForMarkers || {}),
            executions: externalExecutions,
        });
    }, [tradeForMarkers, props?.executions]);
    const replayExecutionSource = useMemo(() => {
        const externalExecutions = Array.isArray(props?.executions) ? props.executions : [];
        if (externalExecutions.length > 0) return externalExecutions;

        if (Array.isArray(tradeForMarkers?.executions) && tradeForMarkers.executions.length > 0) return tradeForMarkers.executions;
        if (Array.isArray(tradeForMarkers?.executionsList) && tradeForMarkers.executionsList.length > 0) return tradeForMarkers.executionsList;
        if (Array.isArray(tradeForMarkers?.fills) && tradeForMarkers.fills.length > 0) return tradeForMarkers.fills;

        return [] as any[];
    }, [props?.executions, tradeForMarkers]);
    const tradeFocusKey = useMemo(() => {
        const primaryId = String(
            tradeForMarkers?.id
            ?? tradeForMarkers?._id
            ?? tradeForMarkers?.tradeId
            ?? tradeForMarkers?.executionId
            ?? ""
        );
        const executionSec = tradeExecutions
            .map((exec) => {
                const raw = Number(exec?.timestamp);
                if (!Number.isFinite(raw) || raw <= 0) return null;
                return raw >= 100000000000 ? Math.floor(raw / 1000) : Math.floor(raw);
            })
            .filter((v): v is number => v != null)
            .sort((a, b) => a - b);

        const first = executionSec[0] ?? canonicalExecutionUnixSec;
        const last = executionSec[executionSec.length - 1] ?? canonicalExecutionUnixSec;
        return `${primaryId}|${first}|${last}|${executionSec.length}`;
    }, [tradeForMarkers, tradeExecutions, canonicalExecutionUnixSec]);

    const chartIdentityKey = activeTradeId ?? tradeFocusKey;

    // auto-focus code removed to disable automatic zoom when selecting a trade
    useEffect(() => {
        // intentionally blank
    }, [timeframe, tradeFocusKey]);

    useEffect(() => {
        setIsPlaying(false);
        setIsReplayMode(false);
        setCurrentIndex(0);
        setDisplayedData([]);
        setHoverData(null);
    }, [chartIdentityKey]);

    // auto-focus handler intentionally disabled (trade selection no longer auto-zooms)
    const applyExecutionAutoFocus = useCallback(() => {
        // no-op
    }, []);

    useEffect(() => {
        if (!pendingExecutionAutoFocusRef.current) return;
        if (isLoading) return;
        if (!chartRef.current) return;
        if (!allData.length || !processedData.length) return;
        if (displayedData.length < allData.length || isPlaying) return;

        applyExecutionAutoFocus();
        pendingExecutionAutoFocusRef.current = false;
    }, [allData.length, processedData.length, displayedData.length, isPlaying, isLoading, applyExecutionAutoFocus]);

    const REPLAY_PREROLL_BARS = 5;
    const REPLAY_BOUNDARY_MODE = DEFAULT_REPLAY_BOUNDARY_MODE;
    const replayMultiplier = useMemo(() => {
        const multiplierSymbol = String(
            tradeForMarkers?.symbol
            ?? replayExecutionSource[0]?.symbol
            ?? symbol
            ?? ''
        );
        const multiplierAssetClass = String(
            tradeForMarkers?.assetClass
            ?? tradeForMarkers?.market
            ?? replayExecutionSource[0]?.assetClass
            ?? secTypeUpper
            ?? ''
        ).toUpperCase();
        return multiplierAssetClass.includes('FUT')
            ? getContractMultiplier(multiplierSymbol, 'FUTURES', replayExecutionSource[0]?.instrument ?? tradeForMarkers?.instrument)
            : 1;
    }, [replayExecutionSource, secTypeUpper, symbol, tradeForMarkers]);
    const normalizedReplayExecutions = useMemo(() => {
        return normalizeReplayExecutions(replayExecutionSource, allData, { multiplier: replayMultiplier, boundaryMode: REPLAY_BOUNDARY_MODE });
    }, [replayExecutionSource, allData, replayMultiplier, REPLAY_BOUNDARY_MODE]);
    const replayRangeTradingDays = useMemo(() => {
        return getReplayRangeTradingDays(currentPolicyTf);
    }, [currentPolicyTf]);
    // The replay range is capped to a trader-friendly window per timeframe
    // (e.g. 5 Trading Days on 15m). The chart data itself is left untouched —
    // only the replay slice is trimmed to the trading-day window that ends at
    // the first execution. `null` means no cap (no executions / Daily).
    const replayRangeCapStartSec = useMemo(() => {
        if (!replayRangeTradingDays || replayRangeTradingDays <= 0) return null;
        const anchorMs = normalizedReplayExecutions[0]?.timestampMs
            ?? (canonicalExecutionUnixSec != null ? canonicalExecutionUnixSec * 1000 : null);
        if (anchorMs == null || !Number.isFinite(anchorMs)) return null;
        return getReplayRangeCapStartSec(anchorMs, replayRangeTradingDays);
    }, [replayRangeTradingDays, normalizedReplayExecutions, canonicalExecutionUnixSec]);
    const replayRangeLabel = useMemo(() => {
        return getReplayRangeLabel(currentPolicyTf) ?? 'Replay';
    }, [currentPolicyTf]);
    const replayStartIndex = useMemo(() => {
        if (replayRangeCapStartSec != null) {
            // First candle inside the capped replay range (the "correct first
            // candle" for this timeframe's replay history).
            const index = allData.findIndex((bar) => Number(bar?.time) >= replayRangeCapStartSec);
            if (index >= 0) return index;
            return Math.max(0, allData.length - 1);
        }
        return getReplayStartIndex(normalizedReplayExecutions[0]?.timestampMs ?? null, allData, REPLAY_PREROLL_BARS, REPLAY_BOUNDARY_MODE);
    }, [replayRangeCapStartSec, allData, normalizedReplayExecutions, REPLAY_PREROLL_BARS, REPLAY_BOUNDARY_MODE]);
    const markerExecutionSource = useMemo(() => {
        if (replayExecutionSource.length > 0) return replayExecutionSource;
        return tradeExecutions;
    }, [replayExecutionSource, tradeExecutions]);
    const rawExecutionMarkers = useMemo(() => {
        if (markerExecutionSource.length === 0 || allData.length === 0) return [] as ExecutionSeriesMarker[];
        return normalizeExecutionsToMarkers(markerExecutionSource, allData, { symbol, secType: secTypeUpper });
    }, [markerExecutionSource, allData, timeframe, symbol, secTypeUpper]);
    const executionMarkers = useMemo(() => {
        if (!executionMarkersVisible) {
            return [] as ExecutionSeriesMarker[];
        }

        // When not replaying, execution markers stay visible (session bar
        // filtering preserves every execution candle).
        if (!isReplayMode) {
            return filterMarkersForDisplay(rawExecutionMarkers, displayedData, false, didFilterBars);
        }

        // During replay we must ensure markers tied to executions are shown according
        // to the replay boundary time. Use the raw execution markers (not the
        // visible-bars-filtered subset) so markers aren't accidentally dropped when
        // the visible bar list doesn't exactly match marker bucket times.
        return filterExecutionMarkersForReplay(rawExecutionMarkers, currentIndex, allData, normalizedReplayExecutions, REPLAY_BOUNDARY_MODE);
    }, [rawExecutionMarkers, displayedData, didFilterBars, isReplayMode, currentIndex, allData, normalizedReplayExecutions, REPLAY_BOUNDARY_MODE, executionMarkersVisible]);

    const updateMarkerBadges = useCallback(() => {
        if (!executionMarkersVisible) {
            setMarkerBadges([]);
            return;
        }

        const chart = chartRef.current;
        const series = candleSeriesRef.current as any;
        if (!chart || !series || executionMarkers.length === 0) {
            setMarkerBadges([]);
            return;
        }

        try {
            const timeScale = (chart as IChartApi).timeScale();
            const containerWidth = containerRef.current?.clientWidth ?? 0;
            const containerHeight = containerRef.current?.clientHeight ?? 0;
            if (containerWidth <= 0 || containerHeight <= 0) {
                setMarkerBadges([]);
                return;
            }

            const badges = layoutExecutionMarkerBadges({
                markers: executionMarkers,
                candleByTime: candleByTimeRef.current,
                containerWidth,
                containerHeight,
                timeToCoordinate: (time: number) => {
                    const coord = timeScale.timeToCoordinate(time as any);
                    return Number.isFinite(Number(coord)) ? Number(coord) : null;
                },
                priceToCoordinate: (price: number) => {
                    const coord = series.priceToCoordinate(price);
                    return Number.isFinite(Number(coord)) ? Number(coord) : null;
                },
            });

            setMarkerBadges(badges);
        } catch {
            setMarkerBadges([]);
        }
    }, [executionMarkers, executionMarkersVisible]);

    const scheduleMarkerBadgeSync = useCallback(() => {
        if (markerOverlayRafRef.current != null) return;
        markerOverlayRafRef.current = window.requestAnimationFrame(() => {
            markerOverlayRafRef.current = null;
            updateMarkerBadges();
        });
    }, [updateMarkerBadges]);

    useEffect(() => {
        return () => {
            if (markerOverlayRafRef.current != null) {
                try { window.cancelAnimationFrame(markerOverlayRafRef.current); } catch {}
                markerOverlayRafRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        scheduleMarkerBadgeSync();
    }, [scheduleMarkerBadgeSync, processedData.length, displayedData.length, chartInitNonce, isReplayMode, currentIndex, timeframe]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        try {
            chart.timeScale().applyOptions({ visible: true });
        } catch {}
    }, [chartInitNonce]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const timeScale = chart.timeScale();
        const sync = () => scheduleMarkerBadgeSync();

        try { timeScale.subscribeVisibleTimeRangeChange(sync); } catch {}
        try { (timeScale as any).subscribeVisibleLogicalRangeChange?.(sync); } catch {}

        return () => {
            try { timeScale.unsubscribeVisibleTimeRangeChange(sync); } catch {}
            try { (timeScale as any).unsubscribeVisibleLogicalRangeChange?.(sync); } catch {}
        };
    }, [scheduleMarkerBadgeSync, chartInitNonce]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const logDiagnostics = () => {
            const timeScale = chart.timeScale();
            const range = timeScale.getVisibleRange();
            const seriesCount = displayedData.length;

            let visibleCount = 0;
            if (range && Number.isFinite(range.from) && Number.isFinite(range.to)) {
                visibleCount = displayedData.filter(d => {
                    const t = Number(d.time);
                    return t >= (range.from as number) && t <= (range.to as number);
                }).length;
            }

            const providerCount = latestMeta?.providerBarCount ?? latestMeta?.barCount ?? 'unknown';
            const backendCount = latestMeta?.windowedBarCount ?? rawData.length ?? 'unknown';
            const firstSeriesTime = displayedData.length > 0 ? formatChartTime(Number(displayedData[0].time), tzMode, true, displayTimeZone) : 'N/A';
            const lastSeriesTime = displayedData.length > 0 ? formatChartTime(Number(displayedData[displayedData.length - 1].time), tzMode, true, displayTimeZone) : 'N/A';
            const firstVisibleTime = range?.from ? formatChartTime(Number(range.from), tzMode, true, displayTimeZone) : 'N/A';
            const lastVisibleTime = range?.to ? formatChartTime(Number(range.to), tzMode, true, displayTimeZone) : 'N/A';

            console.log('[TradeReplayChart Diagnostics]', {
                providerReturnedCount: providerCount,
                backendFinalCount: backendCount,
                frontendSeriesCount: seriesCount,
                visibleCount,
                firstSeriesTime,
                lastSeriesTime,
                firstVisibleTime,
                lastVisibleTime,
                sessionMode: detectedSession,
                tzMode,
                timeframe,
            });
        };

        const timeScale = chart.timeScale();
        try { timeScale.subscribeVisibleTimeRangeChange(logDiagnostics); } catch {}
        logDiagnostics();

        return () => {
            try { timeScale.unsubscribeVisibleTimeRangeChange(logDiagnostics); } catch {}
        };
    }, [displayedData, latestMeta, tzMode, displayTimeZone, detectedSession, timeframe, chartInitNonce, rawData.length]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const sync = () => scheduleMarkerBadgeSync();

        const resizeObserver = new ResizeObserver(sync);
        resizeObserver.observe(el);

        el.addEventListener('wheel', sync, { passive: true });
        window.addEventListener('resize', sync);

        return () => {
            try { resizeObserver.disconnect(); } catch {}
            el.removeEventListener('wheel', sync);
            window.removeEventListener('resize', sync);
        };
    }, [scheduleMarkerBadgeSync, chartInitNonce]);

    const visibleMarkers = useMemo(() => {
        if (!tradeMarkersVisible) return [] as TradeMarker[];
        return filterMarkersForDisplay(allMarkers, processedData, false, didFilterBars);
    }, [allMarkers, tradeMarkersVisible, processedData, didFilterBars]);

    useEffect(() => {
        const times = processedData
            .map((bar) => Number(bar.time))
            .filter((t) => Number.isFinite(t))
            .sort((a, b) => a - b);

        if (times.length >= 2) {
            const step = Math.max(1, Math.round(times[1] - times[0]));
            markerToleranceSecRef.current = step;
        } else {
            markerToleranceSecRef.current = 60;
        }
    }, [processedData]);

    useEffect(() => {
        visibleMarkersRef.current = tradeMarkersVisible ? visibleMarkers : [];
    }, [visibleMarkers, tradeMarkersVisible]);

    useEffect(() => {
        if (tradeMarkersVisible) return;
        setHoveredTradeMarker(null);
        markerHoverIdRef.current = null;
    }, [tradeMarkersVisible]);

    useEffect(() => {
        if (!hoveredTradeMarker) return;
        const stillVisible = visibleMarkers.some((marker) => marker.id === hoveredTradeMarker.id);
        if (!stillVisible) {
            setHoveredTradeMarker(null);
            markerHoverIdRef.current = null;
        }
    }, [visibleMarkers, hoveredTradeMarker]);


  const indicatorDataCache = useMemo(() => {
    const out = new Map<string, any>();
    for (const ind of indicators) {
        if(!ind.visible) continue;
        if(ind.type === "SMA") out.set(ind.id, calculateSMA(processedData, ind.period || 20));
        if(ind.type === "EMA") out.set(ind.id, calculateEMA(processedData, ind.period || 20));
        if(ind.type === "VWAP") out.set(ind.id, calculateVWAP(processedData));
        if(ind.type === "BB") out.set(ind.id, calculateBollingerBands(processedData, ind.period || 20, 2));
    }
    return out;
  }, [processedData, indicators]);

    useEffect(() => {
        const c = new Map<number, Candle>();
        const m = new Map<number, number>();
        for (const d of processedData) {
            const t = Number(d.time);
            if (!Number.isFinite(t)) continue;
            const v = Number(d.volume ?? 0);
            m.set(t, Number.isFinite(v) ? v : 0);
            c.set(t, d);
        }
        volumeByTimeRef.current = m;
        candleByTimeRef.current = c;
    }, [processedData]);

  useEffect(() => {
    if (!volumeSeriesRef.current) return;
    try {
        if (volumeVisible) {
            volumeSeriesRef.current.setData(
                processedData
                    .filter((d: any) => d?.time != null)
                    .map((d: any) => ({
                        time: d.time,
                        value: d.volume,
                        color: Number(d.close) >= Number(d.open)
                            ? "rgba(38, 166, 154, 0.6)"
                            : "rgba(239, 83, 80, 0.6)",
                    }))
            );
            try { volumeSeriesRef.current.series.applyOptions({ visible: true }); } catch {}
        } else {
            volumeSeriesRef.current.setData([]);
            try { volumeSeriesRef.current.series.applyOptions({ visible: false }); } catch {}
        }
    } catch {}
    }, [processedData, volumeVisible]);

    useEffect(() => {
        if (!candleSeriesRef.current) return;

        drawingManagerRef.current?.updateChartSeries(chartRef.current as any, candleSeriesRef.current);

        // Candles
        try {
            const bars = processedData.map((b) => ({
                time: Number(b.time),
                open: Number(b.open),
                high: Number(b.high),
                low: Number(b.low),
                close: Number(b.close),
            }));
            candleSeriesRef.current.setData(bars);
            syncDrawingManagerBarTimes(drawingManagerRef.current as any, bars as any);

            // Framed viewport logic: recalculate visible range to fit the complete
            // detected session for the trade date, with small logical-range padding.
            if (bars.length > 0 && !isReplayMode) {
                requestAnimationFrame(() => {
                    const chart = chartRef.current;
                    if (!chart) return;
                    try {
                        const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', dateStyle: 'short' });
                        const firstIdx = bars.findIndex(d => {
                            const t = Number(d.time);
                            return Number.isFinite(t)
                                && t >= sessionRangeSec.startSec
                                && fmt.format(new Date(t * 1000)) === executionDateYmd;
                        });
                        if (firstIdx !== -1) {
                            let lastIdx = firstIdx;
                            for (let i = bars.length - 1; i >= firstIdx; i--) {
                                const t = Number(bars[i].time);
                                if (Number.isFinite(t) && t < sessionRangeSec.endSec && fmt.format(new Date(t * 1000)) === executionDateYmd) {
                                    lastIdx = i;
                                    break;
                                }
                            }
                            // Provider returned no bars inside the session (e.g. RTH-only
                            // data for a PRE-MARKET trade) — fall back to the trade date.
                            if (lastIdx < firstIdx) {
                                for (let i = bars.length - 1; i >= firstIdx; i--) {
                                    if (fmt.format(new Date(Number(bars[i].time) * 1000)) === executionDateYmd) {
                                        lastIdx = i;
                                        break;
                                    }
                                }
                            }
                            const sessionBarCount = lastIdx - firstIdx + 1;
                            console.log('[TradeReplayChart Viewport framing] Trade date:', executionDateYmd,
                                'session:', detectedSession,
                                'firstIdx:', firstIdx, 'lastIdx:', lastIdx,
                                'sessionBarCount:', sessionBarCount, 'total series:', bars.length);
                            chart.timeScale().setVisibleLogicalRange({
                                // Small padding around the session. The full context series
                                // remains intact; this only controls the initial viewport.
                                from: Math.max(0, firstIdx - 2),
                                to: Math.min(bars.length - 1, lastIdx + 4),
                            });
                        } else {
                            console.log('[TradeReplayChart Viewport framing] Trade date not found in bars; fitting all content.');
                            chart.timeScale().fitContent();
                        }
                    } catch (e) {
                        console.warn('[TradeReplayChart] Failed to set logical range:', e);
                    }
                });
            }
        } catch {}

        // Indicators
        for (const ind of indicators) {
            if (!chartRef.current) continue;

            if (ind.type === "BB") {
                const idUpper = `${ind.id}_upper`;
                const idLower = `${ind.id}_lower`;
                const bb = indicatorDataCache.get(ind.id) as { upper: any[]; lower: any[] } | undefined;

                let seriesUpper = lineSeriesRefs.current.get(idUpper);
                let seriesLower = lineSeriesRefs.current.get(idLower);

                if (ind.visible && bb) {
                    if (!seriesUpper) {
                        seriesUpper = safeAddLineSeries(chartRef.current, { color: ind.color, lineWidth: 1, title: 'BB Upper' });
                        if (seriesUpper) lineSeriesRefs.current.set(idUpper, seriesUpper);
                    }
                    if (!seriesLower) {
                        seriesLower = safeAddLineSeries(chartRef.current, { color: ind.color, lineWidth: 1, title: 'BB Lower' });
                        if (seriesLower) lineSeriesRefs.current.set(idLower, seriesLower);
                    }
                    try { if (seriesUpper) seriesUpper.setData(bb.upper); } catch {}
                    try { if (seriesLower) seriesLower.setData(bb.lower); } catch {}
                } else {
                    if (seriesUpper) { safeRemoveSeries(chartRef.current, seriesUpper); lineSeriesRefs.current.delete(idUpper); }
                    if (seriesLower) { safeRemoveSeries(chartRef.current, seriesLower); lineSeriesRefs.current.delete(idLower); }
                }
                continue;
            }

            const seriesId = ind.id;
            let series = lineSeriesRefs.current.get(seriesId);

            if (ind.visible) {
                const data = indicatorDataCache.get(ind.id) as any[] | undefined;
                if (!data || data.length === 0) continue;

                if (!series) {
                    series = safeAddLineSeries(chartRef.current, { color: ind.color, lineWidth: 1, title: ind.label });
                    if (series) lineSeriesRefs.current.set(seriesId, series);
                }
                try { if (series) series.setData(data); } catch {}
            } else if (series) {
                safeRemoveSeries(chartRef.current, series);
                lineSeriesRefs.current.delete(seriesId);
            }
        }

        // MACD Update
        if (showMACD && macdChartRef.current && macdSeriesRefs.current.macd) {
            if (processedData.length === 0) {
                try { macdSeriesRefs.current.macd?.setData?.([]); } catch {}
                try { macdSeriesRefs.current.signal?.setData?.([]); } catch {}
                try { macdSeriesRefs.current.histogram?.setData?.([]); } catch {}
            } else {
                try {
                    const { macd, signal, histogram } = calculateMACD(processedData);

                    const safeMacd = macd.filter((d: any) => d && d.time != null && Number.isFinite(d.value));
                    try { macdSeriesRefs.current.macd?.setData?.(safeMacd); } catch {}

                    const safeSignal = signal.filter((d: any) => d && d.time != null && Number.isFinite(d.value));
                    try { macdSeriesRefs.current.signal?.setData?.(safeSignal); } catch {}

                    try {
                        macdSeriesRefs.current.histogram?.applyOptions?.({ color: DEFAULT_HIST_COLOR });
                    } catch {}

                    const safeHist = histogram
                        .map((h: any) => {
                            const time = Number(h?.time);
                            if (!Number.isFinite(time)) return null;
                            const value = safeNum(h?.value);
                            const color = safeBarColor(h?.color);
                            return color ? { time, value, color } : { time, value };
                        })
                        .filter((h: any) => h && Number.isFinite(h.time) && Number.isFinite(h.value));

                    try { macdSeriesRefs.current.histogram?.setData?.(safeHist); } catch {}
                } catch {
                    try { macdSeriesRefs.current.macd?.setData?.([]); } catch {}
                    try { macdSeriesRefs.current.signal?.setData?.([]); } catch {}
                    try { macdSeriesRefs.current.histogram?.setData?.([]); } catch {}
                }
            }
        }

        // Toggle Drawings Visibility
        if (candleSeriesRef.current && drawingsRef.current.length > 0) {
            drawingsRef.current.forEach((drawing) => {
                const drawable = drawing?.kind === 'hline' ? drawing.line : drawing?.series;
                if (drawable?.applyOptions) {
                    try {
                        drawable.applyOptions({ lineVisible: !drawingsHidden, axisLabelVisible: !drawingsHidden });
                    } catch {}
                }
            });
        }
    }, [processedData, volumeVisible, indicators, indicatorDataCache, showMACD, drawingsHidden, isReplayMode, executionDateYmd, sessionRangeSec, detectedSession]);

    useEffect(() => {
        const manager = drawingManagerRef.current;
        const chart = chartRef.current;
        const candleSeries = candleSeriesRef.current;
        if (!manager || !chart || !candleSeries) return;
        manager.updateChartSeries(chart, candleSeries);
        syncDrawingManagerBarTimes(manager as any, processedData as any);
    }, [chartInitNonce, timeframe, detectedSession, sessionRangeSec, displayedData.length, processedData]);

  // --- REPLAY ENGINE ---
  const replayPointCount = useMemo(() => {
      if (!allData.length) return 0;
      return Math.max(1, allData.length - replayStartIndex);
  }, [allData.length, replayStartIndex]);
  const replayRelativeIndex = useMemo(() => {
      if (!allData.length) return 0;
      return Math.max(0, currentIndex - replayStartIndex);
  }, [allData.length, currentIndex, replayStartIndex]);
  const applyReplayViewport = useCallback((activeLogicalIndex: number, fallbackSpan = 120) => {
      const chart = chartRef.current;
      if (!chart) return;

      try {
          const timeScale = chart.timeScale();
          const logicalRange = timeScale.getVisibleLogicalRange();
          const span = logicalRange && Number.isFinite(logicalRange.to) && Number.isFinite(logicalRange.from)
              ? Math.max(20, logicalRange.to - logicalRange.from)
              : fallbackSpan;
          const leftSpan = Math.max(8, Math.floor(span * 0.6));
          const rightSpan = Math.max(8, Math.floor(span * 0.4));

          timeScale.setVisibleLogicalRange({
              from: Math.max(-leftSpan, activeLogicalIndex - leftSpan),
              to: activeLogicalIndex + rightSpan,
          });
      } catch {}
  }, []);
    const replayEndIndex = useMemo(() => Math.max(0, allData.length - 1), [allData.length]);
  const replayStartLabel = useMemo(() => {
      const first = Number(allData[replayStartIndex]?.time);
      if (!Number.isFinite(first)) return '--:--:--';
      return formatChartTimeCompactDate(first, tzMode, displayTimeZone);
  }, [allData, replayStartIndex, tzMode, displayTimeZone]);
  const replayEndLabel = useMemo(() => {
      const last = Number(allData[allData.length - 1]?.time);
      if (!Number.isFinite(last)) return '--:--:--';
      return formatChartTimeCompactDate(last, tzMode, displayTimeZone);
  }, [allData, tzMode, displayTimeZone]);
  const replayProgressPct = useMemo(() => {
      if (replayPointCount <= 1) return 0;
      return (replayRelativeIndex / (replayPointCount - 1)) * 100;
  }, [replayRelativeIndex, replayPointCount]);
    const replayStepBoundary = useMemo(() => getReplayStepBoundaryMs(currentIndex, allData, REPLAY_BOUNDARY_MODE), [currentIndex, allData, REPLAY_BOUNDARY_MODE]);
    const replayCutoffMs = useMemo(() => replayStepBoundary.replayBoundaryTimeMs ?? 0, [replayStepBoundary.replayBoundaryTimeMs]);

  const replayEventMarkers = useMemo(() => {
      if (!allData.length || !normalizedReplayExecutions.length) return [] as Array<{ index: number; color: string; label: string }>;

            // Compute cumulative position to classify each execution as an entry, scale-in, scale-out, or close.
            const markers = [] as Array<{ index: number; color: string; label: string }>;
            let cumulative = 0;
            normalizedReplayExecutions
                .slice()
                .sort((a, b) => (a.timestampMs - b.timestampMs) || (a.originalIndex - b.originalIndex))
                .forEach((exec, idx) => {
                    const prevCum = cumulative;
                    const signed = Number(exec.signedQty || 0);
                    const afterCum = prevCum + signed;

                    let kind = 'fill';
                    if (prevCum === 0) {
                        kind = 'entry';
                    } else if (afterCum === 0) {
                        kind = 'close';
                    } else if (signed > 0) {
                        kind = 'scale-in';
                    } else if (signed < 0) {
                        kind = 'scale-out';
                    }

                    let color = 'bg-slate-400';
                    let label = `Event #${idx + 1}`;
                    if (kind === 'entry') {
                        color = 'bg-blue-500';
                        label = signed > 0 ? 'Long Entry' : 'Short Entry';
                    } else if (kind === 'scale-in') {
                        color = 'bg-emerald-500';
                        label = 'Scale In';
                    } else if (kind === 'scale-out') {
                        color = 'bg-rose-500';
                        label = 'Scale Out';
                    } else if (kind === 'close') {
                        color = 'bg-gray-400';
                        label = 'Close';
                    }

                    markers.push({ index: exec.candleIndex, color, label: `${label} #${idx + 1}` });
                    cumulative = afterCum;
                });

            // Keep chronological order by candle index
            return markers.sort((a, b) => a.index - b.index);
  }, [allData, normalizedReplayExecutions]);

  const setReplayIndex = useCallback((nextIndex: number) => {
      if (!allData.length) return;
      const minIndex = isReplayMode ? replayStartIndex : 0;
      const clamped = Math.max(minIndex, Math.min(allData.length - 1, Math.floor(nextIndex)));
      setCurrentIndex(clamped);
      if (isReplayMode) {
          setDisplayedData(allData.slice(replayStartIndex, clamped + 1));
      }
  }, [allData, isReplayMode, replayStartIndex]);

  useEffect(() => {
      const requestedExecutionId = String((focusExecutionRequest as any)?.executionId ?? '').trim();
      const requestedTsMs = Number((focusExecutionRequest as any)?.timestampMs);
      const requestedPulseNonce = Number((focusExecutionRequest as any)?.pulseNonce ?? 0);
      if ((!requestedExecutionId && (!Number.isFinite(requestedTsMs) || requestedTsMs <= 0))) return;
      if (!allData.length || !chartRef.current) return;

      const focusKey = `${requestedExecutionId}|${requestedPulseNonce}`;
      // if we've already handled this exact focus request, do not re-apply (prevents re-centering on marker/visible updates)
      if (lastHandledFocusKeyRef.current === focusKey) return;

      const requestedUnixSec = requestedTsMs > 1_000_000_000_000
          ? Math.floor(requestedTsMs / 1000)
          : Math.floor(requestedTsMs);

      const exactBadge = requestedExecutionId
          ? markerBadges.find((badge) => String(badge.id) === requestedExecutionId) ?? null
          : null;
      const focusUnixSec = exactBadge?.markerTimeSec ?? requestedUnixSec;

      // one-time visual focus (state-driven); we mark this request as handled so it won't re-fire on badge/visible updates
      setFocusedExecutionId(exactBadge ? String(exactBadge.id) : null);
      setFocusPulseNonce((prev) => prev + 1);
      if (focusPulseTimerRef.current != null) {
          try { window.clearTimeout(focusPulseTimerRef.current); } catch {}
      }
      focusPulseTimerRef.current = window.setTimeout(() => {
          setFocusedExecutionId(null);
          focusPulseTimerRef.current = null;
      }, 2100);

      let closestIndex = 0;
      let closestDiff = Number.POSITIVE_INFINITY;
      for (let index = 0; index < allData.length; index += 1) {
          const barTimeRaw = Number((allData[index] as any)?.time);
          if (!Number.isFinite(barTimeRaw)) continue;
          const barSec = barTimeRaw > 1_000_000_000_000 ? Math.floor(barTimeRaw / 1000) : Math.floor(barTimeRaw);
          const diff = Math.abs(barSec - focusUnixSec);
          if (diff < closestDiff) {
              closestDiff = diff;
              closestIndex = index;
          }
      }

      if (isReplayMode) {
          setReplayIndex(closestIndex);
      }

      try {
          const chart = chartRef.current;
          if (!chart) return;
          const logicalRange = chart.timeScale().getVisibleLogicalRange();
          const span = logicalRange && Number.isFinite(logicalRange.to) && Number.isFinite(logicalRange.from)
              ? Math.max(20, logicalRange.to - logicalRange.from)
              : 120;
          const leftSpan = Math.max(8, Math.floor(span * 0.6));
          const rightSpan = Math.max(8, Math.floor(span * 0.4));
          chart.timeScale().setVisibleLogicalRange({
              from: Math.max(-leftSpan, closestIndex - leftSpan),
              to: closestIndex + rightSpan,
          });
      } catch {}

      lastHandledFocusKeyRef.current = focusKey;
  }, [focusExecutionRequest, allData, markerBadges, isReplayMode, setReplayIndex]);

  useEffect(() => {
      return () => {
          if (focusPulseTimerRef.current != null) {
              try { window.clearTimeout(focusPulseTimerRef.current); } catch {}
              focusPulseTimerRef.current = null;
          }
      };
  }, []);

    // duplicate clearFocusedExecution removed (earlier implementation in file is used)

  const handleLabelClick = useCallback((badgeId: string) => {
      // Clicking a label should only pulse the associated anchor/dot and optionally brighten its connector/label.
      // Do not pan/recenter the chart here — eye icon handles that.
      try {
          if (focusPulseTimerRef.current != null) {
              window.clearTimeout(focusPulseTimerRef.current);
              focusPulseTimerRef.current = null;
          }
      } catch {}

      setFocusedExecutionId(String(badgeId));
      setFocusPulseNonce((n) => n + 1);

      focusPulseTimerRef.current = window.setTimeout(() => {
          focusPulseTimerRef.current = null;
          try { setFocusedExecutionId(null); } catch {}
      }, 2100);
  }, []);

  const handleSeek = useCallback((clientX: number) => {
      if (isLoading) return;
      const el = scrubberRef.current;
      if (!el || replayPointCount <= 1) return;
      const rect = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setReplayIndex(replayStartIndex + (pct * (replayPointCount - 1)));
  }, [isLoading, replayPointCount, replayStartIndex, setReplayIndex]);

  const jumpToNextEvent = useCallback(() => {
      if (!replayEventMarkers.length) {
          setReplayIndex(replayEndIndex);
          return;
      }
      const next = replayEventMarkers.find((m) => m.index > currentIndex);
      setReplayIndex(next ? next.index : replayEndIndex);
  }, [replayEventMarkers, currentIndex, replayEndIndex, setReplayIndex]);

  const replayTradeState = useMemo(() => {
      return buildReplayStepSnapshot({
          currentBarIndex: currentIndex,
          replayStartIndex,
          bars: allData,
          executions: normalizedReplayExecutions,
          multiplier: replayMultiplier,
          markPrice: getReplayMarkPrice(allData[currentIndex] ?? null),
          boundaryMode: REPLAY_BOUNDARY_MODE,
      });
  }, [allData, currentIndex, normalizedReplayExecutions, replayMultiplier, replayStartIndex, REPLAY_BOUNDARY_MODE]);

  const replayDebugEnabled = useMemo(() => {
      if (!import.meta.env.DEV || typeof window === 'undefined') return false;
      try {
          return window.localStorage.getItem('tt_replay_debug') === '1';
      } catch {
          return false;
      }
  }, []);

  useEffect(() => {
      if (!replayDebugEnabled || !isReplayMode) return;
      try {
          console.debug('[TradeReplay] step', {
              currentBarIndex: currentIndex,
              currentBarTime: Number(allData[currentIndex]?.time) || null,
              replayStartIndex,
              stepStartMs: replayTradeState.stepStartMs,
              stepEndMs: replayTradeState.stepEndMs,
              replayBoundaryTimeMs: replayTradeState.replayBoundaryTimeMs,
              appliedExecutionIds: replayTradeState.appliedExecutionIds,
              netQuantity: replayTradeState.netQuantity,
              currentPositionQty: replayTradeState.currentPositionQty,
              side: replayTradeState.currentSide,
              avgEntryPrice: replayTradeState.avgEntryPrice,
              realizedPnl: replayTradeState.realizedPnl,
              unrealizedPnl: replayTradeState.unrealizedPnl,
              totalPnl: replayTradeState.totalPnl,
              currentMarkPrice: getReplayMarkPrice(allData[currentIndex] ?? null),
          });
      } catch {}
  }, [replayDebugEnabled, isReplayMode, currentIndex, replayStartIndex, replayTradeState, allData]);

  const rawPnl = useMemo(() => {
      if (isReplayMode) return replayTradeState.totalPnl;

      // Prefer explicit PnL fields when provided on the trade object
      const explicit = Number(tradeForMarkers?.pnl ?? tradeForMarkers?.netPnl ?? tradeForMarkers?.realizedPnl);
      if (Number.isFinite(explicit) && explicit !== 0) return explicit;

      // Otherwise, attempt to compute from execution-level resolved rows (prefer net)
      const execList = Array.isArray(tradeForMarkers?.executionsList)
          ? tradeForMarkers.executionsList
          : Array.isArray(tradeForMarkers?.executions)
              ? tradeForMarkers.executions
              : Array.isArray(tradeForMarkers?.fills)
                  ? tradeForMarkers.fills
                  : [];

      if (execList.length > 0) {
          try {
              const sample = execList[0] as any;
              const symbol = String(sample?.symbol ?? tradeForMarkers?.symbol ?? '');
              const assetClass = String(sample?.assetClass ?? tradeForMarkers?.assetClass ?? tradeForMarkers?.market ?? '').toUpperCase();
              const multiplier = assetClass.includes('FUT')
                  ? getContractMultiplier(symbol, 'FUTURES', sample?.instrument ?? tradeForMarkers?.instrument)
                  : 1;

              const resolved = resolveExecutionRows(execList as any[], { multiplier });
              const totalNet = resolved.reduce((acc, r) => acc + (Number.isFinite(Number(r.rowRealizedNet)) ? Number(r.rowRealizedNet) : 0), 0);
              return totalNet;
          } catch {
              // fallback to explicit fields if resolver fails
              return Number.isFinite(explicit) ? explicit : 0;
          }
      }

      // final fallback
      return Number.isFinite(explicit) ? explicit : 0;
    }, [isReplayMode, replayTradeState.totalPnl, tradeForMarkers]);
  const isProfit = rawPnl >= 0;
  const pnlDisplay = `${isProfit ? '+' : '-'}$${Math.abs(rawPnl).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
  })}`;
  const formatSignedPnl = (value: number) => `${value >= 0 ? '+' : '-'}$${Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
  })}`;
  const realizedIsProfit = replayTradeState.realizedPnl >= 0;
  const unrealizedIsProfit = replayTradeState.unrealizedPnl >= 0;
  const realizedDisplay = formatSignedPnl(replayTradeState.realizedPnl);
  const unrealizedDisplay = formatSignedPnl(replayTradeState.unrealizedPnl);
    const replayAllowed = canUseBarsForReplay(chartResolution) && !isReplayTimeframeDisabled(timeframe);
    const metricsAllowed = Boolean(chartResolution.metricsAllowed) && canUseBarsForDeterministicMetrics(chartResolution);
    const isDailyTimeframe = isReplayTimeframeDisabled(timeframe);
    const chartWarningMessage = chartResolution.warningMessage || getChartWarningMessage(chartResolution);
        const loadMessageView = formatTradeChartLoadMessage(loadMsg, { meta: latestMeta, resolution: chartResolution });
  const toolbarSymbolLabel = useMemo(() => {
      const explicitDisplay = String(propDisplaySymbol || '').trim().toUpperCase();
      if (secTypeUpper === 'FUT' && explicitDisplay) return explicitDisplay;

      const root = String(symbol || '').trim().toUpperCase();
      if (secTypeUpper !== 'FUT') return root || 'SYM';

      const exactContract = String(
          chartResolution.resolvedLocalSymbol
          || chartResolution.requestedLocalSymbol
          || (propTrade as any)?.localSymbol
          || (propSelectedTrade as any)?.localSymbol
          || ''
      ).trim().toUpperCase();

      if (!root && exactContract) return exactContract;
      if (!exactContract || exactContract === root) return root || 'SYM';
      return `${root} (${exactContract})`;
    }, [symbol, secTypeUpper, chartResolution, propTrade, propSelectedTrade, propDisplaySymbol]);

  useEffect(() => {
      if (typeof onReplayStateChange !== 'function') return;
      try {
          onReplayStateChange({
              isReplayMode,
              isLoading,
              isPlaying,
              chartResolution,
              priceMismatch: chartResolution.warningCode === 'EXECUTION_PRICE_MISMATCH',
              replayAllowed,
              metricsAllowed,
              currentIndex,
              replayTimeSec: Number(allData[currentIndex]?.time) || null,
              replayTimeMs: isReplayMode ? replayCutoffMs : null,
              visibleExecutionCount: replayTradeState.executedCount,
              replayTradeState,
              currentMarkPrice: getReplayMarkPrice(allData[currentIndex] ?? null),
              metricBars: metricsAllowed ? (allData || []).map((bar: any) => ({
                  time: Number(bar?.time) > 1_000_000_000_000 ? Number(bar?.time) : Number(bar?.time) * 1000,
                  high: Number(bar?.high ?? bar?.h),
                  low: Number(bar?.low ?? bar?.l),
              })).filter((bar: any) => Number.isFinite(bar.time) && Number.isFinite(bar.high) && Number.isFinite(bar.low)) : [],
          });
      } catch {}
  }, [onReplayStateChange, isReplayMode, isLoading, isPlaying, currentIndex, allData, replayCutoffMs, replayTradeState, chartResolution, replayAllowed, metricsAllowed]);

  useEffect(() => {
      if (replayAllowed) return;
      setIsPlaying(false);
      setIsReplayMode(false);
  }, [replayAllowed]);

  useEffect(() => {
      if (!allData.length) {
          setCurrentIndex(0);
          setDisplayedData([]);
          setIsPlaying(false);
          setIsReplayMode(false);
          return;
      }

      if (!isReplayMode) {
          setDisplayedData(allData);
          setCurrentIndex(allData.length - 1);
          return;
      }

      const maxIndex = allData.length - 1;
      const minIndex = Math.min(replayStartIndex, maxIndex);
      const clamped = Math.max(minIndex, Math.min(currentIndex, maxIndex));
      if (clamped !== currentIndex) {
          setCurrentIndex(clamped);
          return;
      }
      setDisplayedData(allData.slice(replayStartIndex, clamped + 1));
  }, [allData, isReplayMode, currentIndex, replayStartIndex]);

  useEffect(() => {
      if (!isReplayMode || !chartSettings.followMode) return;
      const chart = chartRef.current;
      if (!chart) return;

      const raf = requestAnimationFrame(() => {
          try {
              applyReplayViewport(replayRelativeIndex);
          } catch {}
      });

      return () => cancelAnimationFrame(raf);
  }, [applyReplayViewport, isReplayMode, chartSettings.followMode, replayRelativeIndex]);

  useEffect(() => {
      if (!isReplayMode || !isPlaying || replayPointCount <= 1) {
          if (replayTimerRef.current) {
              window.clearInterval(replayTimerRef.current);
              replayTimerRef.current = null;
          }
          return;
      }

      if (currentIndex >= replayEndIndex) {
          setIsPlaying(false);
          return;
      }

      const delayMap: Record<'1x' | '2x' | '3x' | '5x', number> = {
          '1x': 1000,
          '2x': 500,
          '3x': 333,
          '5x': 200,
      };

      replayTimerRef.current = window.setInterval(() => {
          setCurrentIndex((prev) => {
              if (prev >= replayEndIndex) {
                  setIsPlaying(false);
                  return prev;
              }
              return prev + 1;
          });
      }, delayMap[replaySpeed] ?? 1000);

      return () => {
          if (replayTimerRef.current) {
              window.clearInterval(replayTimerRef.current);
              replayTimerRef.current = null;
          }
      };
    }, [isReplayMode, isPlaying, replayPointCount, currentIndex, replayEndIndex, replaySpeed]);

  // --- NOTE DRAGGING LOGIC ---
  const handleNoteMouseDown = (e: React.MouseEvent, id: number) => {
      e.stopPropagation();
      if(editingNoteId === id) return;
      setDraggingNoteId(id);
  };

  const handleContainerMouseMove = (e: React.MouseEvent) => {
      if (draggingNoteId !== null && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          setNotes(prev => prev.map(n => n.id === draggingNoteId ? { ...n, x, y } : n));
      }
  };

  const handleContainerMouseUp = () => {
      setDraggingNoteId(null);
  };

  const deleteNote = (id: number, e: React.MouseEvent) => {
      e.stopPropagation();
      setNotes(prev => prev.filter(n => n.id !== id));
  }

  const beginEditNote = (id: number, currentText: string) => {
      setEditingNoteId(id);
      setEditingNoteText(currentText ?? "");
  };

  const commitEditNote = () => {
      if(editingNoteId == null) return;
      setNotes(prev => prev.map(n => n.id === editingNoteId ? { ...n, text: editingNoteText } : n));
      setEditingNoteId(null);
  };

  const cancelEditNote = () => {
      setEditingNoteId(null);
  };

    // Ensure chart measures its container after layout changes (replay start/stop).
    useEffect(() => {
        if (!containerRef.current || !chartRef.current) return;
        const raf = requestAnimationFrame(() => {
            if (!containerRef.current || !chartRef.current) return;
            const { width, height } = containerRef.current.getBoundingClientRect();
            if (width > 0 && height > 0) {
                try { chartRef.current?.applyOptions({ width, height }); } catch {}
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [isReplayMode]);

  // --- HANDLERS ---
  const toggleReplayMode = () => {
        if (!replayAllowed && !isReplayMode) return;
    if (isReplayMode) {
        setIsPlaying(false);
        setIsReplayMode(false);
        setDisplayedData([...allData]);
        setCurrentIndex(Math.max(0, allData.length - 1));
        setTimeout(() => {
             try { chartRef.current?.timeScale?.().fitContent?.(); } catch {}
        }, 50);
    } else {
        if (!allData.length) return;
        setIsPlaying(false);
        setIsReplayMode(true);
        setCurrentIndex(replayStartIndex);
            setDisplayedData(allData.slice(replayStartIndex, replayStartIndex + 1));
            if (chartSettings.followMode) {
                // Ensure initial replay view shows a centered replay window instead
                // of pinning the first candle near the right edge.
                const INITIAL_WINDOW = 90;
                requestAnimationFrame(() => {
                    try {
                        applyReplayViewport(0, INITIAL_WINDOW);
                    } catch {}
                });
            }
    }
  };

  useEffect(() => {
      const series = candleSeriesRef.current;
      if (!series) return;

      const existing = replayBreakEvenLineRef.current;
      if (existing) {
          try {
              if (typeof series.removePriceLine === 'function') series.removePriceLine(existing);
              else if (typeof existing.remove === 'function') existing.remove();
          } catch {}
          replayBreakEvenLineRef.current = null;
      }

      if (!isReplayMode || replayTradeState.closed || !Number.isFinite(Number(replayTradeState.breakEvenPrice))) return;

      try {
          replayBreakEvenLineRef.current = series.createPriceLine?.({
              price: Number(replayTradeState.breakEvenPrice),
              color: '#facc15',
              lineWidth: 2,
              lineStyle: 0,
              axisLabelVisible: true,
              title: 'Replay BE',
          }) ?? null;
      } catch {}
  }, [isReplayMode, replayTradeState.breakEvenPrice, replayTradeState.closed]);

  const saveChart = () => {
    setShowSaveMsg(true);
    setTimeout(() => setShowSaveMsg(false), 2000);
  };

  const clearDrawings = () => {
      const toDelete = [...symbolDrawingsRef.current, ...tradeDrawingsRef.current];
      setSymbolDrawings([]);
      setTradeDrawings([]);
      setNotes([]);

      toDelete.forEach((drawing) => {
          persistDeleteDrawing(drawing.id);
      });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
        const el = wrapperRef.current ?? document.documentElement;
        el.requestFullscreen?.().catch(() => {});
    } else {
        document.exitFullscreen?.();
    }
  };

  useEffect(() => {
      const onFsChange = () => {
          try {
              const fsEl = document.fullscreenElement;
              const isFs = !!fsEl && (fsEl === wrapperRef.current || fsEl === document.documentElement || fsEl === containerRef.current);
              setIsFullscreen(Boolean(isFs));

              const chart = chartRef.current;
              const el = containerRef.current;
              if (chart && el) {
                  const w = Math.round(el.clientWidth);
                  const h = Math.round(el.clientHeight);
                  try { chart.applyOptions({ width: w, height: h }); } catch {}
              }
          } catch {}
      };

      document.addEventListener('fullscreenchange', onFsChange);
      return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);



  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
        if (!isGlobalDeleteAllowed(e, editingNoteId)) return;
        const candidate = draggedLineRef.current ?? (lineEdit ? lineEdit.line : null);
        if(!candidate) return;

        const series = candleSeriesRef.current;
        try {
            if(series && typeof series.removePriceLine === 'function') {
                series.removePriceLine(candidate);
            } else if (candidate && typeof candidate.remove === 'function') {
                try { candidate.remove(); } catch {}
            }
        } catch {}

        try {
            drawingsRef.current = drawingsRef.current.filter((drawing) => drawing?.kind !== 'hline' || drawing.line !== candidate);
        } catch {}

        if(draggedLineRef.current === candidate) {
            draggedLineRef.current = null;
            setDraggedLine(null);
        }
        if(lineEdit && lineEdit.line === candidate) {
            setLineEdit(null);
        }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lineEdit]);

    const toggleIndicator = (id: string) => {
        setChartSettings({
            defaultIndicatorIds: toggleChartIndicatorSettingId(chartSettings.defaultIndicatorIds, id as any),
        });
  };

  const handleZoomReset = () => {
        if (isReplayMode) {
                try {
                        applyReplayViewport(replayRelativeIndex);
                } catch {}
                return;
        }
        try { chartRef.current?.timeScale?.().fitContent?.(); } catch {}
  };

    const handleZoomIn = () => {
        const chart = chartRef.current;
        if (!chart) return;
        try {
                const timeScale = chart.timeScale();
                const logicalRange = timeScale.getVisibleLogicalRange();
                if (logicalRange) {
                        const span = logicalRange.to - logicalRange.from;
                        const newSpan = span * 0.8;
                        const center = (logicalRange.from + logicalRange.to) / 2;
                        timeScale.setVisibleLogicalRange({
                                from: center - newSpan / 2,
                                to: center + newSpan / 2,
                        });
                }
        } catch {}
    };
  const handleZoomOut = () => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
        const timeScale = chart.timeScale();
        const logicalRange = timeScale.getVisibleLogicalRange();
        if (logicalRange) {
            const span = logicalRange.to - logicalRange.from;
            const newSpan = span * 1.25;
            const center = (logicalRange.from + logicalRange.to) / 2;
            timeScale.setVisibleLogicalRange({
                from: center - newSpan / 2,
                to: center + newSpan / 2,
            });
        }
    } catch {}
  };

  const takeScreenshot = () => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
        const canvas = chart.takeScreenshot();
        downloadDataUrl(canvas.toDataURL(), "chart.png");
    } catch {}
  };

  const handleIndClick = () => {
      if (indBtnRef.current) {
          const rect = indBtnRef.current.getBoundingClientRect();
          setDropdownPos({ top: rect.bottom + 5, left: rect.left });
      }
      setShowIndDropdown(!showIndDropdown);
  };

    return (
                                        <div ref={wrapperRef} className={`flex w-full ${dark ? 'bg-[#131722] text-gray-300' : 'bg-white text-zinc-900'} font-sans overflow-hidden`}>
                        <style>{`@keyframes tt-focus-pulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.45); opacity: 0.9; } 100% { transform: scale(1); opacity: 1; } }`}</style>

        {/* --- LEFT SIDEBAR (Drawing Tools) --- */}
        <div className={dark ? "w-12 flex flex-col items-center py-3 gap-2 border-r border-[#242428] bg-[#222226] z-20" : "w-12 flex flex-col items-center py-3 gap-2 border-r border-zinc-200 bg-zinc-100 z-20"}>
            <TooltipButton side="right" onClick={() => setDrawMode("select")} active={drawMode === "select"} title="Select"><Crosshair /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("note")} active={drawMode === "note"} title="Note"><Type className="h-6 w-6" /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("rect")} active={drawMode === "rect"} title="Zone (Rectangle)">
                <TransformSquare size={22} strokeWidth={2} className="h-6 w-6" />
            </TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("hline")} active={drawMode === "hline"} title="Horizontal Line"><HorizontalLineNode size={22} strokeWidth={2} className="h-6 w-6" /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("vline")} active={drawMode === "vline"} title="Vertical Line"><VerticalLineNode size={22} strokeWidth={2} className="h-6 w-6" /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("trend")} active={drawMode === "trend"} title="Trendline"><DiagonalLineNodes size={22} strokeWidth={2} className="h-6 w-6" /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("ray")} active={drawMode === "ray"} title="Ray"><DiagonalRayNode size={22} strokeWidth={2} className="h-6 w-6" /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawMode("channel")} active={drawMode === "channel"} title="Parallel Channel"><ParallelChannelNodes size={22} strokeWidth={2} className="h-6 w-6" /></TooltipButton>

            <div className={dark ? "w-8 h-px bg-gray-700 my-1" : "w-8 h-px bg-zinc-200 my-1"}></div>

            <TooltipButton side="right" onClick={handleZoomIn} title="Zoom In"><ZoomIn /></TooltipButton>
            <TooltipButton side="right" onClick={handleZoomOut} title="Zoom Out"><ZoomOut /></TooltipButton>
            <TooltipButton side="right" onClick={handleZoomReset} title="Reset Zoom"><ScanSearch /></TooltipButton>

            <div className="w-8 h-px bg-gray-700 my-1"></div>

            <TooltipButton side="right" onClick={() => setMagnetEnabled(!magnetEnabled)} active={magnetEnabled} title="Magnet Mode"><Magnet /></TooltipButton>
            <TooltipButton side="right" onClick={() => setDrawingsLocked(!drawingsLocked)} active={drawingsLocked} title="Lock Drawings"><Lock /></TooltipButton>
            <TooltipButton side="right" onClick={clearDrawings} className="text-red-400 hover:text-red-300" title="Clear All Drawings"><Trash2 /></TooltipButton>

            <div className="flex-1"></div>
        </div>

        {/* --- MAIN CONTENT --- */}
        <div className="flex-1 flex flex-col min-w-0">
            {/* TOP TOOLBAR */}
            <div className={`relative flex items-center gap-2 p-2 ${dark ? 'bg-[#222226] border-b border-[#242428]' : 'bg-zinc-100 border-b border-zinc-200'} overflow-x-auto whitespace-nowrap`}>
                {/* DEV BUILD STAMP — visible badge to confirm HMR bundle. Remove before shipping. */}
                {import.meta.env.DEV && (
<span
  className="pointer-events-none absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-purple-700 px-2 py-1 text-[10px] font-medium text-white"
>                        BUILD {DEV_BUILD_ID} · {displayTimeZone} · {detectedSession} · {replayRangeLabel}
                    </span>
                )}
                <span
                    className={`font-bold px-3 mr-4 select-none text-[27px] ${dark ? 'text-white' : 'text-zinc-900'}`}
                >
                    {toolbarSymbolLabel}
                </span>
                <div className="flex items-center">
                    <label className="sr-only" htmlFor="timeframeSelect">Timeframe</label>
                    <select
                        id="timeframeSelect"
                        value={TF_TO_POLICY[timeframe]}
                        onChange={(e) => {
                            const policyTf = e.target.value as PolicyTf;
                            const tf = POLICY_TO_TF[policyTf];
                            if (tf) setTimeframe(tf);
                        }}
                        className={`text-xs px-2 py-1 rounded border ${dark ? 'bg-[#222226] border-gray-600 text-gray-200 hover:bg-[#2a2e39]' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50'} focus:outline-none`}
                    >
                        {TIMEFRAME_POLICY_ORDER.map((policyTf) => {
                            const isValid = validTimeframes[policyTf] !== false;
                            return (
                                <option
                                    key={policyTf}
                                    value={policyTf}
                                    disabled={!isValid}
                                    title={!isValid ? 'Trade duration too long for this timeframe.' : undefined}
                                >
                                    {policyTf === '60m' ? '1H' : policyTf}
                                </option>
                            );
                        })}
                    </select>
                </div>

                <span className={`ml-1 text-xs ${dark ? 'text-gray-400' : 'text-zinc-500'}`} title="Display timezone is configured in Settings">{displayTimeZone}</span>

                <div className={`h-5 w-px ${dark ? 'bg-gray-600' : 'bg-zinc-200'} mx-1`}></div>

                <TooltipButton side="bottom" onClick={() => setCandleMode(m => m === "candle" ? "heikin" : "candle")} active={candleMode === "heikin"} title="Candles / Heikin"><ChartCandlestick /></TooltipButton>
                <TooltipButton side="bottom" onClick={() => setShowVolume(v => !v)} active={volumeVisible} title="Toggle Volume"><ChartNoAxesColumn /></TooltipButton>

                <div className={`h-5 w-px ${dark ? 'bg-gray-600' : 'bg-zinc-200'} mx-1`}></div>

                <div className="">
                    <button ref={indBtnRef} onClick={handleIndClick} className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded ${dark ? 'hover:bg-[#2a2e39] text-gray-300' : 'hover:bg-blue-500 hover:text-white text-zinc-600'}`}>
                        <Layers />
                        <span>Indicators</span>
                    </button>
                    {showIndDropdown && (
                        <div className={`fixed w-40 rounded shadow-xl z-50 flex flex-col p-1 border ${dark ? 'bg-[#222226] border-gray-600' : 'bg-zinc-100 border-zinc-200'}`} style={{ top: dropdownPos.top, left: dropdownPos.left }}>
                            {indicators.map(ind => (
                                <button key={ind.id} onClick={() => toggleIndicator(ind.id)} className={`text-xs text-left px-3 py-2 rounded ${dark ? 'hover:bg-[#2a2e39]' : 'hover:bg-blue-500 hover:text-white'} ${ind.visible ? 'text-blue-400 font-bold' : dark ? 'text-gray-400' : 'text-zinc-600'}`}>{ind.label}</button>
                            ))}
                            <div className={`h-px ${dark ? 'bg-gray-700' : 'bg-zinc-200'} my-1`}></div>
                            <button onClick={() => toggleIndicator('macd')} className={`text-xs text-left px-3 py-2 rounded ${dark ? 'hover:bg-[#2a2e39]' : 'hover:bg-blue-500 hover:text-white'} ${showMACD ? 'text-blue-400 font-bold' : dark ? 'text-gray-400' : 'text-zinc-600'}`}>MACD</button>
                        </div>
                    )}
                </div>
                {showIndDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowIndDropdown(false)}></div>}

                <div className="flex-1" />

                {replayUsageModel && (
                    <div className="shrink-0 ml-2">
                        <div
                            data-testid="replay-usage-badge"
                            aria-hidden="false"
                            aria-label={replayUsageBadgeHelp}
                            title={replayUsageBadgeHelp}
                            className={`inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs border whitespace-nowrap ${dark ? 'bg-[#111214] text-gray-200 border-[#26282c]' : 'bg-white text-zinc-800 border-zinc-200'}`}
                        >
                            <span className="leading-none font-medium">Replay uses left</span>
                            <span className="font-mono font-bold">{replayUsageRemainingDisplay} / {replayUsageModel.allowanceGranted}</span>
                            {replayUsageModel.isExhausted && (
                                <span className="text-[11px] text-amber-500 ml-2">Allowance used</span>
                            )}
                            {!replayUsageModel.isExhausted && replayUsageModel.isNearLimit && (
                                <span className="text-[11px] text-amber-500 ml-2">Low</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Local time hidden per UX request */}
                <TooltipButton side="bottom" onClick={() => setChartSettings({ lockScrolling: !chartSettings.lockScrolling })} active={scrollLocked} title="Lock scrolling"><Lock /></TooltipButton>
                <TooltipButton side="bottom" onClick={() => setChartSettings({ showLegend: !chartSettings.showLegend })} active={chartSettings.showLegend} title="Toggle Legend"><Eye /></TooltipButton>

                <div className={`h-5 w-px ${dark ? 'bg-gray-600' : 'bg-zinc-200'} mx-1`}></div>

                <TooltipButton side="bottom" onClick={saveChart} className="hover:text-green-400" title="Save Chart"><Save /></TooltipButton>
                <TooltipButton side="bottom" onClick={takeScreenshot} title="Screenshot"><Camera /></TooltipButton>
                <TooltipButton side="bottom" onClick={toggleFullscreen} title="Fullscreen">{isFullscreen ? <Minimize2 /> : <Maximize2 />}</TooltipButton>
            </div>

            {/* CHART AREA */}
            <div
                ref={chartAreaRef}
                className="relative w-full min-h-0 flex-1 flex flex-col overflow-hidden"
                onWheel={scrollLocked ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                } : undefined}
                onTouchMove={scrollLocked ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                } : undefined}
            >
                <div className="w-full min-h-0 flex flex-col" style={isFullscreen ? { height: '100%' } : { height: isReplayMode ? '640px' : '700px', minHeight: isReplayMode ? '460px' : '500px', maxHeight: '1000px', boxSizing: 'border-box' }}>
                    <div className="relative flex-1 min-w-0">
                        <div className="absolute top-1 left-1 right-2 z-30 pointer-events-none select-none">
                            <div className="flex items-start gap-2">
                                {chartSettings.showLegend && hoverData && (
                                    <div className={`p-2 rounded border text-xs shadow-lg flex flex-col gap-1 pointer-events-none select-none ${dark ? 'bg-[#222226] border-gray-700' : 'bg-zinc-100 border-zinc-200'}`}>
                                        <div className="flex gap-3 font-mono">
                                            <span className="flex items-baseline gap-1">
                                                <span className="font-semibold" style={dark ? undefined : { color: hoverData.open > hoverData.close ? 'rgb(239,83,80)' : 'rgb(38,166,154)' }}>O:</span>
                                                <span style={{ color: hoverData.open > hoverData.close ? 'rgb(239,83,80)' : 'rgb(38,166,154)' }}>{hoverData.open.toFixed(2)}</span>
                                            </span>
                                            <span className="flex items-baseline gap-1">
                                                <span className="font-semibold" style={dark ? undefined : { color: 'rgb(38,166,154)' }}>H:</span>
                                                <span style={{ color: 'rgb(38,166,154)' }}>{hoverData.high.toFixed(2)}</span>
                                            </span>
                                            <span className="flex items-baseline gap-1">
                                                <span className="font-semibold" style={dark ? undefined : { color: 'rgb(239,83,80)' }}>L:</span>
                                                <span style={{ color: 'rgb(239,83,80)' }}>{hoverData.low.toFixed(2)}</span>
                                            </span>
                                            <span className="flex items-baseline gap-1">
                                                <span className="font-semibold" style={dark ? undefined : { color: hoverData.open > hoverData.close ? 'rgb(239,83,80)' : 'rgb(38,166,154)' }}>C:</span>
                                                <span style={{ color: hoverData.open > hoverData.close ? 'rgb(239,83,80)' : 'rgb(38,166,154)' }}>{hoverData.close.toFixed(2)}</span>
                                            </span>
                                        </div>
                                        <div className={`flex gap-3 font-mono pt-0.5 ${dark ? 'text-gray-400' : 'text-zinc-700'}`}>
                                            <span><span className="font-semibold">Time:</span> {Number.isFinite(Number(hoverData?.time)) ? formatChartTime(Number(hoverData.time), tzMode, true, displayTimeZone) : ''}</span>
                                            <span><span className="font-semibold">Vol:</span> {hoverData?.volume?.toLocaleString?.() ?? ''}</span>
                                        </div>
                                    </div>
                                )}
                                <TradeChartStateBadge meta={latestMeta} resolution={chartResolution} className="pointer-events-auto shrink-0 self-start" />
                            </div>
                            {(timeframeAdjustedMsg || toolWarningMsg || loadMsg || showSaveMsg) && (
                                <div className="flex justify-center px-6" style={{ marginTop: '-23px' }}>
                                    {timeframeAdjustedMsg ? (
                                        <span className="text-xs text-blue-300 transition-opacity duration-300 text-center truncate">{timeframeAdjustedMsg}</span>
                                    ) : toolWarningMsg ? (
                                        <span className="text-xs text-amber-300 transition-opacity duration-300 text-center truncate">{toolWarningMsg}</span>
                                    ) : loadMsg ? (
                                        <span
                                            className={`inline-flex max-w-full items-center justify-center rounded-md border px-3 py-1 text-xs shadow-sm text-center truncate ${dark ? 'bg-[#111214]/95 text-gray-200 border-[#26282c]' : 'bg-white/95 text-zinc-700 border-zinc-200'}`}
                                            title={loadMessageView.title}
                                        >
                                            {loadMessageView.text}
                                        </span>
                                    ) : (
                                        showSaveMsg && <span className="text-green-400 text-xs font-bold animate-pulse text-center truncate">Chart Saved!</span>
                                    )}
                                </div>
                            )}
                        </div>
                <div
                    ref={containerRef}
                    className={"w-full relative cursor-crosshair overflow-hidden " + (isFullscreen ? "flex-1" : "")}
                    style={isFullscreen ? { boxSizing: 'border-box', height: '100%' } : { height: isReplayMode ? 'calc(100% - 1px)' : '100%', minHeight: '0', maxHeight: 'none', boxSizing: 'border-box' }}
                    onMouseMove={handleContainerMouseMove}
                    onMouseUp={handleContainerMouseUp}
                    onPointerDown={() => {
                        // Clear any focused/pulsing execution when the user clicks/touches the chart area.
                        // Label clicks stopPropagation so this will not run for label interactions.
                        try { if (focusPulseTimerRef.current != null) { window.clearTimeout(focusPulseTimerRef.current); focusPulseTimerRef.current = null; } } catch {}
                        try { setFocusedExecutionId(null); } catch {}
                    }}
                >
                    {(!isLoading && chartResolution.chartMode === 'unavailable') && (
                        <div
                            data-testid={isDemoTradeActive ? "demo-chart-unavailable-state" : "chart-unavailable-state"}
                            className={`absolute inset-0 flex flex-col items-center justify-center text-center p-6 z-35 pointer-events-auto ${dark ? 'bg-[#1a1a1c]/95 text-zinc-100' : 'bg-white/95 text-zinc-900'}`}
                        >
                            <div className="max-w-md flex flex-col items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${dark ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                                    <span className="text-xl">📊</span>
                                </div>
                                {chartResolution.warningCode === 'INVALID_EQUITY_MARKET_DATE' ? (
                                    <div>
                                        <h3 className="text-base font-semibold leading-6">Market closed for this trade date</h3>
                                        <p className={`mt-2 text-sm ${dark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                            {chartResolution.warningMessage || 'The U.S. equity market was closed on this trade date.'}
                                        </p>
                                        <p className={`mt-1 text-xs ${dark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                            The original trade timestamp was preserved; no chart-provider request was made.
                                        </p>
                                    </div>
                                ) : isDemoTradeActive ? (
                                    <div>
                                        <h3 className="text-base font-semibold leading-6">Demo chart data unavailable</h3>
                                        <p className={`mt-2 text-sm ${dark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                            This is a sample demo trade. Historical chart replay is not available for demo trades yet.
                                        </p>
                                        <p className={`mt-1 text-xs ${dark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                            Real imported trades will show chart/replay data when market data is available.
                                        </p>
                                    </div>
                                ) : (chartResolution.warningCode === 'INDEX_CHART_UNAVAILABLE' || isIndexInstrumentChart) ? (
                                    <div>
                                        <h3 className="text-base font-semibold leading-6">Chart data unavailable for this instrument.</h3>
                                        <p className={`mt-2 text-sm ${dark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                            Importing, journaling, and performance analytics remain available for this trade.
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        <h3 className="text-base font-semibold leading-6">Chart data unavailable</h3>
                                        <p className={`mt-2 text-sm ${dark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                            Replay is still in beta. Try another symbol or date range if data is unavailable.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {(chartWarningMessage && !(chartResolution.chartMode === 'unavailable' && chartResolution.barsAvailable === false)) ? (
                        <div
                            role="status"
                            aria-live="polite"
                            className="absolute z-40 left-1/2 transform -translate-x-1/2 top-14 pointer-events-none"
                        >
                            <div className={`mx-auto inline-flex backdrop-blur-sm text-[13px] rounded-lg px-4 py-3 border shadow-md items-center gap-4 pointer-events-auto ${dark ? (chartResolution.chartMode === 'approximate' ? 'bg-violet-950/75 text-violet-100 border-violet-500/60' : chartResolution.chartMode === 'unavailable' ? 'bg-rose-950/75 text-rose-100 border-rose-500/60' : 'bg-amber-950/75 text-amber-100 border-amber-500/60') : (chartResolution.chartMode === 'approximate' ? 'bg-violet-50 text-violet-900 border-violet-200' : chartResolution.chartMode === 'unavailable' ? 'bg-rose-50 text-rose-900 border-rose-200' : 'bg-amber-50 text-amber-900 border-amber-200')}`}>
                                <span className="text-lg leading-none">⚠</span>
                                <div className="text-left">
                                    <div className="font-semibold">{chartWarningMessage}</div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* <HistoricalDataWarning meta={latestMeta} timeframe={timeframe} candlesLength={allData.length} /> */}

                    <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden" aria-hidden="true">
                        {executionMarkersVisible && markerBadges.map((badge) => {
                            const dx = badge.connectorX - badge.anchorX;
                            const dy = badge.connectorY - badge.anchorY;
                            const length = Math.max(0, Math.hypot(dx, dy));
                            const angle = Math.atan2(dy, dx) * (180 / Math.PI);
                            const isFocusedAnchor = focusedExecutionId != null && String(badge.id) === String(focusedExecutionId);
                            const pointerLeft = Math.max(6, Math.min(badge.width - 6, badge.connectorX - (badge.left - badge.width / 2)));
                            const isBuyBadge = String(badge.color || '').toLowerCase() !== '#dc2626' && String(badge.color || '').toLowerCase() !== '#ef4444' && String(badge.color || '').toLowerCase() !== '#f87171';
                            const badgeFill = dark ? 'rgba(22, 26, 34, 0.9)' : 'rgba(255, 255, 255, 0.96)';
                            const badgeBorder = dark ? (isBuyBadge ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)') : (isBuyBadge ? 'rgba(167, 243, 208, 1)' : 'rgba(254, 202, 202, 1)');
                            const badgeText = dark ? (isBuyBadge ? 'rgba(52, 211, 153, 1)' : 'rgba(248, 113, 113, 1)') : (isBuyBadge ? 'rgba(4, 120, 87, 1)' : 'rgba(185, 28, 28, 1)');
                            return (
                                <div key={badge.id} className="absolute inset-0 pointer-events-none">
                                    <div
                                        style={{
                                            left: `${badge.anchorX}px`,
                                            top: `${badge.anchorY}px`,
                                        }}
                                        className="absolute z-[31] pointer-events-none transform -translate-x-1/2 -translate-y-1/2"
                                    >
                                        {isFocusedAnchor ? (
                                            (() => {
                                                const raw = String(badge.actionType || '');
                                                const norm = raw.toLowerCase().replace(/[_\s]/g, '').replace(/-/g, '');
                                                let actionText = 'OPEN';

                                                if (norm === 'open') {
                                                    actionText = 'OPEN';
                                                } else if (norm === 'scalein') {
                                                    actionText = 'SCALE-IN';
                                                } else if (norm === 'scaleout') {
                                                    actionText = 'SCALE-OUT';
                                                } else if (norm === 'close') {
                                                    actionText = 'CLOSE';
                                                } else {
                                                    actionText = raw.toString().toUpperCase().slice(0, 12);
                                                }

                                                // Use the same visual family as the main marker labels:
                                                // theme-aware translucent fill, subtle border; action color used only as accent (text + border tint).
                                                // Use the same class patterns as the Action column chips to match shape and color family.
                                                const actionClass = actionText === 'OPEN'
                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                                                    : actionText === 'SCALE-IN'
                                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700'
                                                        : actionText === 'SCALE-OUT'
                                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700'
                                                            : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-600';

                                                return (
                                                    <div
                                                        className={`absolute z-40 pointer-events-none select-none inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${actionClass}`}
                                                        style={{
                                                            left: '50%',
                                                            top: '-30px',
                                                            transform: 'translateX(-50%)',
                                                            background: badgeFill,
                                                            minWidth: 44,
                                                            textAlign: 'center',
                                                            whiteSpace: 'nowrap',
                                                            boxShadow: '0 1px 4px rgba(0,0,0,0.5)'
                                                        }}
                                                    >
                                                        <span className="uppercase text-[11px] tracking-wide leading-none">{actionText}</span>
                                                    </div>
                                                );
                                            })()
                                        ) : null}
                                        <div
                                            style={{
                                                width: isFocusedAnchor ? '10px' : '8px',
                                                height: isFocusedAnchor ? '10px' : '8px',
                                                borderRadius: '9999px',
                                                backgroundColor: (
                                                    badge.actionType === 'open' ? '#2563eb' :
                                                    badge.actionType === 'scaleIn' ? '#16a34a' :
                                                    badge.actionType === 'scaleOut' ? '#dc2626' :
                                                    badge.actionType === 'close' ? '#6b7280' :
                                                    (badge.side === 'sell' ? '#dc2626' : '#16a34a')
                                                ),
                                                border: '1px solid rgba(255,255,255,0.9)',
                                                boxShadow: isFocusedAnchor
                                                    ? '0 0 6px rgba(250,204,21,0.12), 0 0 18px rgba(250,204,21,0.14), 0 0 0 3px rgba(59,130,246,0.18)'
                                                    : badge.actionType === 'open' ? '0 0 6px rgba(37,99,235,0.12)'
                                                    : badge.actionType === 'scaleIn' ? '0 0 6px rgba(16,185,129,0.12)'
                                                    : badge.actionType === 'scaleOut' ? '0 0 6px rgba(220,38,38,0.12)'
                                                    : badge.actionType === 'close' ? '0 0 6px rgba(107,114,128,0.08)'
                                                    : (badge.side === 'sell' ? '0 0 6px rgba(220,38,38,0.12)' : '0 0 6px rgba(16,185,129,0.12)'),
                                                ...(isFocusedAnchor ? { animation: 'tt-focus-pulse 700ms ease-in-out 0s 3' } : {}),
                                                ...(badge as any).anchorStyle || {},
                                            }}
                                            data-focus-nonce={isFocusedAnchor ? focusPulseNonce : undefined}
                                            className={`pointer-events-none transition-none`}
                                            title={
                                                badge.actionType && badge.actionType !== 'none'
                                                    ? `Action: ${badge.actionType.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}`
                                                    : undefined
                                            }
                                        />
                                    </div>
                                    {length > 6 && (
                                        (() => {
                                            const total = length;
                                            const preferredLabelStub = 26;
                                            const minGap = 6; // keep a small visual gap from the anchor region

                                            let labelStub = Math.min(preferredLabelStub, total - minGap);
                                            if (labelStub + minGap > total) {
                                                const avail = Math.max(0, total - minGap);
                                                labelStub = Math.max(2, avail);
                                            }

                                            // label stub: short segment starting at the badge connector and pointing toward the anchor
                                            const labelRot = angle + 180; // reverse direction
                                            const labelLeft = badge.connectorX;
                                            const labelTop = badge.connectorY;

                                            return (
                                                <>
                                                    {(() => {
                                                        const cleanId = String(badge.id).replace(/[^a-zA-Z0-9_-]/g, '_');
                                                        const labelMarkerId = `lbl_arrow_${cleanId}`;
                                                        const halfH = 4; // svg height half

                                                        const labelSvgStyle: React.CSSProperties = {
                                                            left: `${labelLeft}px`,
                                                            top: `${labelTop - halfH}px`,
                                                            width: `${labelStub}px`,
                                                            height: `${halfH * 2}px`,
                                                            transformOrigin: 'left center',
                                                            transform: `rotate(${labelRot}deg)`,
                                                            overflow: 'visible',
                                                        };

                                                        return (
                                                            <>
                                                                <svg className="absolute z-[29] pointer-events-none" style={labelSvgStyle} xmlns="http://www.w3.org/2000/svg">
                                                                    <defs>
                                                                        <marker id={labelMarkerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                                                                            <path d="M 0 0 L 10 5 L 0 10 z" fill={markerConnectorColor(badge.color)} opacity={isFocusedAnchor ? "1" : "0.95"} />
                                                                        </marker>
                                                                    </defs>
                                                                    <line x1="0" y1="5" x2="100%" y2="5" stroke={markerConnectorColor(badge.color)} strokeWidth={isFocusedAnchor ? "2" : "1.25"} strokeLinecap="round" strokeOpacity={isFocusedAnchor ? "1" : "0.85"} markerEnd={`url(#${labelMarkerId})`} />
                                                                </svg>
                                                            </>
                                                        );
                                                    })()}
                                                </>
                                            );
                                        })()
                                    )}
                                    <div
                                        title={badge.title}
                                        className="absolute z-30 pointer-events-auto select-none -translate-x-1/2 text-[11px] leading-tight px-2 py-[4px] rounded-md border font-bold whitespace-nowrap shadow-[0_1px_4px_rgba(0,0,0,0.18)] backdrop-blur-sm"
                                        style={{
                                            left: `${badge.left}px`,
                                            top: `${badge.top}px`,
                                            backgroundColor: badgeFill,
                                            borderColor: isFocusedAnchor ? (isBuyBadge ? 'rgba(52,211,153,0.95)' : 'rgba(248,113,113,0.95)') : badgeBorder,
                                            color: badgeText,
                                            paddingTop: '4px',
                                            paddingBottom: '4px',
                                            boxShadow: isFocusedAnchor ? '0 0 10px rgba(250,204,21,0.16), 0 0 24px rgba(59,130,246,0.12)' : undefined,
                                            transition: 'box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
                                            cursor: 'pointer',
                                        }}
                                        onPointerDown={(e) => { e.stopPropagation(); }}
                                        onClick={(e) => { e.stopPropagation(); handleLabelClick(String(badge.id)); }}
                                    >
                                        <span
                                            className="absolute block w-0 h-0"
                                            style={badge.pointerDirection === 'up'
                                                ? {
                                                    left: `${pointerLeft}px`,
                                                    top: '-6px',
                                                    transform: 'translateX(-50%)',
                                                    borderLeft: '4px solid transparent',
                                                    borderRight: '4px solid transparent',
                                                    borderBottom: `6px solid ${badgeFill}`,
                                                }
                                                : {
                                                    left: `${pointerLeft}px`,
                                                    bottom: '-6px',
                                                    transform: 'translateX(-50%)',
                                                    borderLeft: '4px solid transparent',
                                                    borderRight: '4px solid transparent',
                                                    borderTop: `6px solid ${badgeFill}`,
                                                }}
                                        />
                                        {badge.text}
                                    </div>

                                    {/* Legend trigger (interactive) - small info 'i' with hover tooltip */}
                                    <div className="absolute top-3 right-20 z-50 pointer-events-auto">
                                        <div className="relative group">
                                            <div className="w-6 h-6 rounded-full text-zinc-800 flex items-center justify-center text-sm font-medium cursor-default" title="Execution legend" style={{ background: dark ? undefined : undefined }}>
                                                <div className={`${dark ? 'bg-zinc-800/70 border-zinc-600 text-amber-400' : 'bg-orange-300 border-orange-300'} w-6 h-6 rounded-full flex items-center justify-center`}>i</div>
                                            </div>
                                            <div className="absolute right-0 -top-1 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-150" style={{ minWidth: 180 }}>
                                                <div className={`text-sm rounded-md border p-2 shadow-md ${dark ? 'bg-zinc-900 text-zinc-100 border-zinc-700' : 'bg-white text-zinc-900 border-zinc-200'}`}>
                                                    <div className="font-semibold mb-1">Execution legend</div>
                                                    <div className="flex flex-col gap-1">
                                                        <div><span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: '#2563eb', border: '1px solid rgba(255,255,255,0.9)' }} />Open</div>
                                                        <div><span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: '#16a34a', border: '1px solid rgba(255,255,255,0.9)' }} />Scale In</div>
                                                        <div><span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: '#dc2626', border: '1px solid rgba(255,255,255,0.9)' }} />Scale Out</div>
                                                        <div><span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: '#6b7280', border: '1px solid rgba(255,255,255,0.9)' }} />Close</div>
                                                        <div><span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: '#f59e0b', border: '1px solid rgba(255,255,255,0.9)' }} />Replay BE - Break Even</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!isLoading && !chartRef.current && (
                                <div className={`absolute inset-0 flex flex-col items-center justify-center text-xs gap-2 z-20 ${dark ? 'bg-black/30 text-white' : 'bg-white/75 text-zinc-800'}`}>
                            <div className="font-semibold">Chart not initialized</div>
                            <div className="text-gray-200">{chartInitError || "No chart instance"}</div>
                            <div className="text-gray-400">candles: {displayedData.length} | symbol: {symbol || "(none)"}</div>
                            <button
                                className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs"
                                onClick={() => setChartInitNonce((n) => n + 1)}
                            >
                                Retry chart init
                            </button>
                        </div>
                    )}

                    {/* Render Sticky Notes */}
                    {!drawingsHidden && notes.map(note => (
                        <div
                            key={note.id}
                            className="absolute z-30 bg-[#facc15] text-black text-xs px-2 py-1 rounded shadow-lg font-bold border border-yellow-600 cursor-move group select-none flex items-center gap-2"
                            style={{ top: note.y, left: note.x, transform: 'translate(-50%, -50%)' }}
                            onMouseDown={(e) => handleNoteMouseDown(e, note.id)}
                            onDoubleClick={() => beginEditNote(note.id, note.text)}
                        >
                            {editingNoteId === note.id ? (
                                          <textarea
                                              aria-label="Edit replay note"
                                   autoFocus
                                   value={editingNoteText}
                                   rows={Math.max(2, editingNoteText.split('\n').length)}
                                   onChange={e => setEditingNoteText(e.target.value)}
                                   onBlur={commitEditNote}
                                   onKeyDown={e => {
                                       if(e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commitEditNote();
                                       if(e.key === 'Escape') cancelEditNote();
                                   }}
                                   onMouseDown={e => e.stopPropagation()}
                                   className="bg-white/90 text-black text-xs font-semibold px-2 py-1 rounded border border-yellow-700 focus:outline-none resize-none min-w-[140px]"
                                />
                            ) : (
                                <span className="whitespace-pre-wrap">{note.text}</span>
                            )}

                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded px-1">
                                <div
                                    className="p-1 cursor-pointer hover:text-red-700"
                                    onClick={(e) => deleteNote(note.id, e)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Delete Note"
                                >
                                    <X />
                                </div>
                            </div>
                        </div>
                    ))}

                    {lineEdit ? (
                        <div
                          className="absolute z-40"
                          style={{
                              top: lineEdit.y,
                              left: lineEdit.x,
                              transform: 'translate(-50%, -120%)'
                          }}
                          onMouseDown={e => e.stopPropagation()}
                        >
                             <input
                                autoFocus
                                value={lineEdit.value}
                                onChange={e => setLineEdit({ ...lineEdit, value: e.target.value })}
                                onBlur={() => {
                                    try { lineEdit.line?.applyOptions?.({ title: lineEdit.value }); } catch {}
                                    setLineEdit(null);
                                }}
                                onKeyDown={e => {
                                    if(e.key === 'Enter') {
                                        try { lineEdit.line?.applyOptions?.({ title: lineEdit.value }); } catch {}
                                        setLineEdit(null);
                                    }
                                    if(e.key === 'Escape') setLineEdit(null);
                                }}
                                className={`text-xs px-2 py-1 rounded border focus:outline-none shadow-lg ${dark ? 'bg-[#111827] text-white border-blue-400' : 'bg-white text-zinc-800 border-blue-300'}`}
                                placeholder="Level label"
                             />
                        </div>
                    ) : null}

                    {drawMode === "hline" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-amber-600 text-white border border-amber-400' : 'bg-amber-50 text-amber-900 border border-amber-200'}`}>Click to place Horizontal Line</div>}
                    {drawMode === "note" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-indigo-600 text-white border border-indigo-400' : 'bg-indigo-50 text-indigo-900 border border-indigo-200'}`}>Click to place Note • Double-click note to edit</div>}
                    {drawMode === "rect" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-blue-600 text-white border border-blue-400' : 'bg-blue-50 text-blue-900 border border-blue-200'}`}>Click and drag to place Zone</div>}
                    {drawMode === "trend" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-blue-600 text-white border border-blue-400' : 'bg-blue-50 text-blue-900 border border-blue-200'}`}>Trendline: click point 1, then point 2</div>}
                    {drawMode === "ray" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-blue-600 text-white border border-blue-400' : 'bg-blue-50 text-blue-900 border border-blue-200'}`}>Ray: click point 1, then point 2</div>}
                    {drawMode === "channel" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-emerald-600 text-white border border-emerald-400' : 'bg-emerald-50 text-emerald-900 border border-emerald-200'}`}>Channel: click base start, base end, then offset point</div>}
                    {drawMode === "vline" && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-violet-600 text-white border border-violet-400' : 'bg-violet-50 text-violet-900 border border-violet-200'}`}>Click to place Vertical Line • Double-click to edit label</div>}
                    {draggedLine && <div className={`absolute top-12 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded shadow-lg animate-pulse pointer-events-none z-30 font-bold ${dark ? 'bg-orange-600 text-white border border-orange-400' : 'bg-orange-50 text-orange-900 border border-orange-200'}`}>Move the line, then click to drop it—or press Delete to remove it.</div>}

                    {/* L2 DOM removed */}

                    {/* Centered loading overlay shown while the chart is (re)fetching,
                        e.g. immediately after a timeframe change. Removed as soon as the
                        chart data is ready (isLoading flips false). */}
                    {isLoading && (
                        <div
                            data-testid="replay-chart-loading-overlay"
                            className="absolute inset-0 z-40 flex items-center justify-center pointer-events-auto"
                            style={{ background: dark ? 'rgba(17,18,20,0.45)' : 'rgba(255,255,255,0.45)' }}
                        >
                            <div className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium shadow-xl ${dark ? 'bg-[#111214]/95 text-gray-100 border-[#26282c]' : 'bg-white/95 text-zinc-800 border-zinc-200'}`}>
                                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
                                Loading chart...
                            </div>
                        </div>
                    )}
                </div>

                {/* MACD */}
                {showMACD && (
                    <div className="h-[180px] w-full border-t border-[#242428] relative" ref={macdContainerRef}>
                        <div className="absolute top-1 left-2 text-[10px] text-gray-500 z-10 font-bold select-none">MACD (12, 26, 9)</div>
                    </div>
                )}
                    </div>
                </div>
                </div>

            {/* PLAYBACK BAR */}
            <div className={`w-full h-[72px] relative flex items-center px-4 shrink-0 shadow-2xl z-20 transition-colors duration-300 ${dark ? 'bg-[#222226] border-t border-[#242428]' : 'bg-zinc-100 border-t border-zinc-200'}`}>
                {pnlOverlayVisible && (
                    <div
                        className={`absolute -translate-x-1/2 h-[66px] w-fit min-w-[260px] px-5 flex items-center justify-center z-30 transition-all duration-300 ease-out ${
                            isReplayMode ? 'bottom-full translate-y-[6px]' : 'bottom-0 translate-y-full opacity-0 pointer-events-none'
                        }`}
                        style={{ left: '50%' }}
                    >
                        <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden" preserveAspectRatio="none" viewBox="0 0 100 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 66 L12 0 L88 0 L100 66" fill={dark ? '#222226' : '#f4f4f5'} stroke={dark ? '#242428' : '#e2e8f0'} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                            <line x1="12" y1="1" x2="88" y2="1" stroke={dark ? '#242428' : '#cbd5e1'} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                        </svg>
                        <div className="relative z-10 flex flex-col items-center justify-end gap-0.5 h-full w-full px-1.5 pt-16 pb-2 whitespace-nowrap overflow-hidden">
                            <div className="flex max-w-full items-center justify-center gap-2 translate-y-[1px]">
                                <span className={`${isProfit ? 'text-emerald-400 drop-shadow-[0_0_12px_rgba(52,211,153,0.5)]' : 'text-rose-400 drop-shadow-[0_0_12px_rgba(251,113,133,0.5)]'} text-[2.45rem] font-mono font-bold leading-none tracking-tight`}>
                                    {pnlDisplay}
                                </span>
                                <TrendingUp className={`${isProfit ? 'text-emerald-500' : 'text-rose-500 rotate-180'} transition-transform duration-300 opacity-90`} size={22} strokeWidth={3} />
                            </div>
                            <div className="mt-[7px] flex max-w-full items-center justify-center gap-1.5 text-[10px] font-bold tracking-[0.12em] uppercase leading-none">
                                <span className={dark ? 'text-gray-500' : 'text-zinc-500'}>Realized</span>
                                <span className={realizedIsProfit ? 'text-emerald-400' : 'text-rose-400'}>{realizedDisplay}</span>
                                <span className={dark ? 'text-gray-600' : 'text-zinc-300'}>·</span>
                                <span className={dark ? 'text-gray-500' : 'text-zinc-500'}>Unrealized</span>
                                <span className={unrealizedIsProfit ? 'text-emerald-400' : 'text-rose-400'}>{unrealizedDisplay}</span>
                            </div>
                        </div>
                    </div>
                )}
                    <div className="shrink-0 z-50">
                    <button
                        onClick={toggleReplayMode}
                        disabled={isLoading || (!replayAllowed && !isReplayMode)}
                        title={!replayAllowed && !isReplayMode ? (isDemoTradeActive ? 'Replay is not available for demo trades.' : (isDailyTimeframe ? "Replay isn't available on the Daily timeframe." : (chartWarningMessage || 'Replay unavailable for the active chart source.'))) : undefined}
                        className={`group flex items-center gap-2.5 px-3.5 py-1.5 border rounded-md transition-all duration-200 shadow-sm ${
                            isReplayMode
                                ? dark
                                    ? 'bg-rose-500/10 border-rose-500/30 hover:bg-rose-500/20 hover:border-rose-500/50'
                                    : 'bg-rose-50 border-rose-200 hover:bg-rose-100 hover:border-rose-300'
                                : replayAllowed
                                    ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400/30 shadow-[0_0_15px_rgba(37,99,235,0.2)]'
                                    : dark
                                        ? 'bg-zinc-700/70 text-zinc-400 border-zinc-600 cursor-not-allowed'
                                        : 'bg-zinc-100 text-zinc-500 border-zinc-200 cursor-not-allowed'
                        }`}
                    >
                        {isReplayMode ? (
                            <>
                                <X className="text-rose-400 group-hover:rotate-90 transition-transform" />
                                <span className={`text-[10px] font-bold tracking-widest uppercase whitespace-nowrap ${dark ? '' : 'text-rose-700'}`}>EXIT REPLAY</span>
                            </>
                        ) : (
                            <>
                                <Play className="group-hover:scale-110 transition-transform" />
                                <span className={`text-[10px] font-bold tracking-widest uppercase whitespace-nowrap ${dark ? '' : 'text-white'}`}>START REPLAY</span>
                            </>
                        )}
                    </button>
                        {/* Explain disabled state for the Daily timeframe */}
                        {!replayAllowed && !isReplayMode && isDailyTimeframe && (
                            <div className={`mt-1 text-xs ${dark ? 'text-gray-400' : 'text-zinc-500'}`}>
                                Replay isn't available on the Daily timeframe. Replay is available on 1m, 5m, 15m, 30m, and 1H.
                            </div>
                        )}
                </div>

                <div className={`relative z-40 flex-1 flex items-center transition-all duration-300 ease-out transform ${
                    isReplayMode ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-12 opacity-0 pointer-events-none'
                }`}>
                    <div className={`h-6 w-[1px] ${dark ? 'bg-[#242428]' : 'bg-zinc-200'} mx-5`}></div>

                    <div className="flex items-center gap-4 shrink-0">
                        <button onClick={() => setReplayIndex(currentIndex - 1)} disabled={isLoading} className={`w-10 h-10 flex items-center justify-center rounded transition-colors ${isLoading ? 'opacity-40 cursor-not-allowed' : ''} ${dark ? 'text-gray-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`} title="Previous Candle">
                            <SkipBack size={18} />
                        </button>
                        <button
                            onClick={() => setIsPlaying((p) => !p)}
                            disabled={isLoading}
                            className={`w-10 h-10 bg-blue-600 hover:bg-blue-500 text-white rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all hover:scale-105 shrink-0 ${isLoading ? 'opacity-40 cursor-not-allowed hover:scale-100' : ''}`}
                        >
                            {isPlaying ? (
                                <Pause className="m-auto block" size={18} strokeWidth={2.5} />
                            ) : (
                                <Play className="m-auto block" size={18} strokeWidth={2.5} />
                            )}
                        </button>
                        <button onClick={() => setReplayIndex(currentIndex + 1)} disabled={isLoading} className={`w-10 h-10 flex items-center justify-center rounded transition-colors ${isLoading ? 'opacity-40 cursor-not-allowed' : ''} ${dark ? 'text-gray-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`} title="Next Candle">
                            <SkipForward size={18} />
                        </button>
                        <button onClick={jumpToNextEvent} disabled={isLoading} className={`group flex items-center gap-2.5 px-4 py-1.5 rounded-md transition-all duration-300 shadow-sm ${isLoading ? 'opacity-40 cursor-not-allowed' : ''} ${dark ? 'bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 hover:border-blue-500/50' : 'bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-300'}`}>
                            <ArrowRightToLine size={18} className="text-blue-400 group-hover:scale-110 transition-transform" />
                            <span className="text-[11px] font-bold text-blue-400/90 tracking-wide uppercase">Next Event</span>
                        </button>
                        {/* replay speed moved to right side */}
                    </div>

                    <div className="mt-1 flex-1 flex items-center justify-end gap-6 pl-8 pr-2">
                        <div className="flex-1 flex flex-col gap-1.5">
                            <div
                                ref={scrubberRef}
                                onPointerDown={(e) => {
                                    e.currentTarget.setPointerCapture(e.pointerId);
                                    handleSeek(e.clientX);
                                }}
                                onPointerMove={(e) => {
                                    if (e.buttons === 1) handleSeek(e.clientX);
                                }}
                                onPointerUp={(e) => {
                                    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                                        e.currentTarget.releasePointerCapture(e.pointerId);
                                    }
                                }}
                                className={`relative w-full h-5 rounded-full border cursor-pointer group touch-none ${dark ? 'bg-[#1a1a1c] border-[#242428]' : 'bg-white border-zinc-300'}`}
                            >
                                <div className="absolute left-0 top-0 bottom-0 bg-blue-600 rounded-full transition-all duration-200" style={{ width: `${replayProgressPct}%` }}></div>
                                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4.5 h-4.5 bg-white border-[2.5px] border-blue-500 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all z-20" style={{ left: `${replayProgressPct}%` }}></div>
                                {executionMarkersVisible && replayEventMarkers.map((marker, i) => (
                                    <div key={`${marker.index}-${i}`} className="absolute top-1/2 -translate-y-1/2 group/marker z-10 hover:z-[100]" style={{ left: `${(Math.max(0, marker.index - replayStartIndex) / Math.max(1, replayPointCount - 1)) * 100}%` }}>
                                        <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-[10px] rounded-md opacity-0 group-hover/marker:opacity-100 pointer-events-none transition-all duration-200 whitespace-nowrap shadow-2xl z-[100] flex items-center gap-2 translate-y-1 group-hover/marker:translate-y-0 ${dark ? 'bg-[#1A202A] border border-[#3B445A] text-gray-100' : 'bg-white border border-zinc-200 text-zinc-800'}`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${marker.color} animate-pulse`} />
                                            <span className="font-bold tracking-wide uppercase">{marker.label}</span>
                                            <div className={`absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent ${dark ? 'border-t-[#3B445A]' : 'border-t-white'}`} />
                                        </div>
                                        <div className={`w-2.5 h-2.5 rounded-full border border-black shadow-sm ${marker.color} transition-all duration-200 group-hover/marker:scale-125 group-hover/marker:ring-4 group-hover/marker:ring-white/10`} />
                                    </div>
                                ))}
                            </div>
                            <div className={`flex justify-between px-1 text-xs font-mono font-bold ${dark ? 'text-gray-400' : 'text-zinc-500'}`}>
                                <span>{replayStartLabel}</span>
                                <span>{replayEndLabel}</span>
                            </div>
                        </div>

                        <div className="shrink-0 ml-4">
                            <div className={`flex items-center gap-0.5 rounded-md p-1 border ${dark ? 'bg-[#1a1a1c] border-[#36363a]' : 'bg-white border-zinc-200'}`}>
                                {(['1x', '2x', '3x', '5x'] as const).map((speed) => (
                                    <button
                                        key={speed}
                                        onClick={() => setReplaySpeed(speed)}
                                        disabled={isLoading}
                                        className={`px-3 py-1 text-[11px] font-bold rounded-[4px] transition-colors ${isLoading ? 'opacity-40 cursor-not-allowed' : ''} ${replaySpeed === speed ? (dark ? 'bg-[#2c2d31] text-white shadow-sm' : 'bg-blue-500 text-white shadow-sm') : (dark ? 'text-gray-500 hover:text-gray-200' : 'text-zinc-500 hover:text-zinc-900')}`}
                                    >
                                        {speed}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 ml-4">
                    {replayDebugEnabled && isReplayMode && (
                        <div className={`mr-4 text-[11px] font-mono px-2 py-1 rounded border ${dark ? 'text-blue-200 bg-[#081222]/70 border-blue-400/20' : 'text-blue-700 bg-blue-50 border-blue-200'}`}>
                            <div className="flex items-center gap-2">
                                <span className={dark ? 'text-gray-300' : 'text-zinc-500'}>raw:</span>
                                <span className="font-bold text-white">{rawExecutionMarkers.length}</span>
                                <span className={dark ? 'text-gray-300' : 'text-zinc-500'}>exec:</span>
                                <span className="font-bold text-white">{normalizedReplayExecutions.length}</span>
                                <span className={dark ? 'text-gray-300' : 'text-zinc-500'}>applied:</span>
                                <span className="font-bold text-white">{executionMarkers.length}</span>
                                <span className={dark ? 'text-gray-300' : 'text-zinc-500'}>visible:</span>
                                <span className="font-bold text-white">{visibleMarkers.length}</span>
                            </div>
                        </div>
                    )}
                    <div className={`h-5 w-px ${dark ? 'bg-gray-600' : 'bg-zinc-200'}`}></div>
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${dark ? 'hover:text-white' : 'text-zinc-700 hover:text-zinc-900'}`}><input type="checkbox" checked={chartSettings.showTradeMarkers} onChange={e => setChartSettings({ showTradeMarkers: e.target.checked })} className="rounded bg-gray-700" /><span>Markers</span></label>
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${dark ? 'hover:text-white' : 'text-zinc-700 hover:text-zinc-900'}`}><input type="checkbox" checked={showTradeDrawings} onChange={e => setChartSettings({ showTradeDrawings: e.target.checked })} className="rounded bg-gray-700" /><span>Trade drawings</span></label>
                    {secTypeUpper === 'STK' && (
                        <div className="flex min-w-[140px] flex-col items-center gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                                Trade Session
                            </span>
                            <div
                                className={`flex h-8 w-full cursor-default select-none items-center justify-center rounded-lg border px-3 text-[11px] font-bold uppercase ${dark ? 'border-slate-700 bg-slate-900/60 text-zinc-200' : 'border-zinc-200 bg-white text-zinc-600'}`}
                                title="Chart session selected automatically from trade execution times."
                                aria-label={`Automatically detected trade session: ${detectedSession}`}
                            >
                                {detectedSession}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );
}

export default React.memo(TradeReplayChart);
